import { Fragment, useCallback, useEffect, useMemo, useState } from "react";

import type {
  FlowWindow,
  ParticipantsStats,
  Token,
  ParticipantCohortKind,
  WalletTier,
} from "../api/types";
import { useMarketWalletTrades } from "../hooks/useMarketWalletTrades";
import { useNow } from "../hooks/useNow";
import { useParticipants } from "../hooks/useParticipants";
import { useApplyWalletTags, useWalletTagDict } from "../hooks/useWalletTags";
import {
  ageLabel,
  emptyNote,
  FLOW_WINDOWS,
  flowNote,
  flowWindowLabel,
  hiddenNote,
  participantLines,
  participantsSummary,
  settlementNote,
  participantWarnings,
  type ParticipantLine,
  type ParticipantSort,
  type ParticipantSortKey,
} from "../lib/participants";
import { holderGroups, type HolderGroup } from "../lib/holders";
import {
  edgeTitle,
  forecastEdge,
  forecastTitle,
  stakeTitle,
  totalsLine,
  totalsNote,
  totalsTitle,
  type TotalsLine,
} from "../lib/participanttotals";
import { tagFilterNote } from "../lib/tagfilter";
import {
  cohortsView,
  COHORT_KINDS,
  sharesColor,
  SHORT_TITLES,
  signOf,
  TITLES,
  type CohortFloor,
  type CohortLine,
  type CohortsView,
} from "../lib/cohorts";
import { plural } from "../lib/format";
import { parseSeriesSlug } from "../lib/series";
import { walletTradeLines, walletTradesNote } from "../lib/wallettrades";
import { useAgainstUs } from "../store/useAgainstUs";
import { useBooks } from "../store/useBooks";
import { useCohortFloor } from "../store/useCohortFloor";
import { useFlowWindow } from "../store/useFlowWindow";
import { useParticipantPicks } from "../store/useParticipantPicks";
import { useParticipantTags, type TagState } from "../store/useParticipantTags";
import {
  useParticipantsView,
  type ParticipantsView,
} from "../store/useParticipantsView";
import { useParticipantSort } from "../store/useParticipantSort";
import { useTiers } from "../store/useTiers";
import { Empty, Panel } from "./Panel";
import { Tier, WalletLabel } from "./Tier";

interface Props {
  conditionId: string;
  /** Исходы рынка. Сопоставление токена с «Up»/«Down» есть только здесь. */
  tokens: readonly Token[];
  /**
   * Слаг рынка — ради НАЧАЛА ОКНА в раскрытом списке сделок.
   *
   * Секунда от старта отвечает на главный вопрос списка («зашёл сразу или
   * дождался конца»), а взять её больше неоткуда: в ответе панели границ окна
   * нет, и заводить их там значило бы завести второе место, где рынок разбирают
   * на серию. Пусто — колонка τ просто не рисуется.
   */
  slug?: string | undefined;
}

/**
 * Кто сколько набрал в этом рынке и какой ролью.
 *
 * # Зачем панель
 *
 * На экране не было видно, КТО торгует: публичный принт не несёт ни одного
 * адреса, а контрагент известен только у наших собственных сделок. Маркет-мейкер
 * при этом защищает крупные сделки, двигая цену в свою сторону, и его накопленная
 * позиция говорит, куда рынок будут тянуть. Колонки «тейкером» и «мейкером» и
 * есть ответ: по одному нетто тот, кто бьёт, неотличим от того, кто впитывает.
 *
 * # Границы применимости, которые обязаны быть на экране
 *
 * Источник — индексатор Polymarket, и он отстаёт на минуты, причём рывками. На
 * пятиминутном окне это значит, что таблица описывает ПРОШЕДШЕЕ окно, а не
 * идущее, поэтому возраст данных стоит в шапке и выделяется, когда переваливает
 * за минуту.
 *
 * Вся логика — в lib/participants.ts, здесь разметка.
 */
export function ParticipantsPanel({ conditionId, tokens, slug }: Props) {
  // Окно статистики живёт в сторе, а не здесь: рынки короткие, и при переходе с
  // окна на окно локальное состояние сбрасывало бы выбор оператора.
  const picked = useFlowWindow((s) => s.picked);
  const pick = useFlowWindow((s) => s.pick);
  // Режим — тоже в сторе и по той же причине: рынки пятиминутные, и локальное
  // состояние сбрасывало бы «против нас» при каждом переходе на новое окно.
  const against = useAgainstUs((s) => s.against);
  const setAgainst = useAgainstUs((s) => s.set);
  // Фильтр по ручным пометкам — тоже стор и по той же причине, что режим: он
  // сужает СОСТАВ строк, и сброс при переходе на новое окно означал бы, что
  // отобранная группа кошельков теряется каждые пять минут.
  //
  // Уезжает В ЗАПРОС, а не режет строки здесь: сервер обрезает выдачу сотней
  // крупнейших, и помеченный кошелёк с маленьким нетто до этой стороны просто не
  // доехал бы (см. useParticipants).
  const tagInclude = useParticipantTags((s) => s.include);
  const tagExclude = useParticipantTags((s) => s.exclude);
  // Фильтр по ПОДГРУППЕ сводки — туда же и по тому же доводу: подгруппу считает
  // тот же проход, что сводку, по всем кошелькам снимка, и режет сервер тоже до
  // отсечки в сотню (см. cohortFilter на той стороне).
  const groupInclude = useParticipantTags((s) => s.groupInclude);
  const groupExclude = useParticipantTags((s) => s.groupExclude);
  const q = useParticipants(
    conditionId,
    picked,
    against,
    tagInclude,
    tagExclude,
    groupInclude,
    groupExclude,
  );
  // Отмеченные галочками кошельки — вход строки «Итого». Стор, а не локальное
  // состояние, и переживает смену рынка: группа наблюдения — свойство КОШЕЛЬКОВ,
  // а не окна (см. store/useParticipantPicks).
  const picks = useParticipantPicks((s) => s.picked);
  // Тир берётся ПО АДРЕСУ из отдельного стора, как в ленте: он приезжает
  // сообщением profile_update и дорисовывает уже нарисованную строку.
  const tiers = useTiers((s) => s.tiers);
  // Порядок строк — тоже в сторе и по той же причине, что режим: рынки
  // пятиминутные, и «поставил по пнл» иначе держалось бы до конца окна.
  const sort = useParticipantSort((s) => s.sort);
  const toggleSort = useParticipantSort((s) => s.toggle);
  // Ступень порога — в сторе по той же причине, что окно и режим: рынки
  // пятиминутные, и локальное состояние сбрасывало бы выбор при каждом переходе.
  const floor = useCohortFloor((s) => s.picked);
  const pickFloor = useCohortFloor((s) => s.pick);
  // Какая разбивка на экране — тоже стор и по той же причине. Почему разбивки
  // не показываются вдвоём, разобрано в store/useParticipantsView.ts.
  const view = useParticipantsView((s) => s.view);
  const setView = useParticipantsView((s) => s.set);

  /**
   * Чьи сделки раскрыты. Локальное состояние, а не стор, и это осознанно:
   * раскрытая строка — вопрос к КОНКРЕТНОМУ окну, и тащить её на следующее
   * значило бы раскрывать список кошелька, которого там может не быть вовсе.
   *
   * Множество, а не один адрес: несколько кошельков раскрываются разом — ради
   * сравнения, ровно того, чего не даёт модальное окно.
   */
  const [openTrades, setOpenTrades] = useState<ReadonlySet<string>>(new Set());
  /**
   * Раскрыт ли подпункт итога «держатели сейчас».
   *
   * Локальное состояние, а не стор, и по тому же доводу, что у раскрытых сделок:
   * это вопрос к КОНКРЕТНОМУ окну — что группа держит здесь и сейчас, — и тащить
   * раскрытие на следующее окно значило бы обещать ответ про рынок, которого
   * ещё нет.
   *
   * Свёрнут по умолчанию: строка итога отвечает на главный вопрос («что держит
   * группа») одним числом, а список держателей — это уже разбор.
   */
  const [holdersOpen, setHoldersOpen] = useState(false);
  const toggleTrades = useCallback((wallet: string) => {
    setOpenTrades((prev) => {
      const next = new Set(prev);
      if (!next.delete(wallet)) next.add(wallet);
      return next;
    });
  }, []);
  // Смена рынка закрывает раскрытое: тот же кошелёк в новом окне — другая
  // история, и показывать под ним старую нельзя.
  useEffect(() => {
    setOpenTrades(new Set());
    setHoldersOpen(false);
  }, [conditionId]);

  // Начало окна для колонки τ. Разбирается один раз на панель, а не в каждой
  // раскрытой строке.
  const windowStart = useMemo(
    () => (slug ? (parseSeriesSlug(slug)?.start ?? null) : null),
    [slug],
  );

  const p = q.data;
  const lines = participantLines(p, tokens, sort);
  const warnings = participantWarnings(p);
  // Отсечка и сортировка вместе обманывают: отбор в список шёл по накоплению, и
  // переставленная таблица читается как срез всего рынка. Это говорится словами.
  const hidden = hiddenNote(p, Boolean(sort));
  const flow = flowNote(p);
  const summary = participantsSummary(p);
  const settled = settlementNote(p);
  const cohorts = cohortsView(p, tokens, floor, against);
  // Итог по отмеченным — либо по всем показанным, когда не отмечено ничего.
  // Считается по СЫРЫМ строкам ответа, а не по `lines`: там нетто уже взято по
  // модулю, и сумма модулей дала бы перевес у группы, стоящей по разные стороны.
  const totals = totalsLine(p, tokens, picks);
  const filterNote = tagFilterNote(p);
  // Кнопка нажата, а сводки нет — в режиме «против нас» её не бывает вовсе.
  // Тогда на экране остаётся таблица, а причина стоит строкой над ней: пустая
  // карточка была бы худшим ответом, чем разбивка, которую не просили.
  const byCohorts = view === "cohorts" && cohorts.lines.length > 0;
  // Подписи берут окно ИЗ ОТВЕТА, а не из выбора: пока новый свод едет, на экране
  // стоят числа за прежнее окно, и подписать их новым значило бы соврать на
  // полсекунды — ровно в том поле, ради честности которого переключатель заведён.
  const shown = p?.flow_window;

  return (
    <Panel
      title={against ? "Накоплено против нас" : "Накоплено на рынке"}
      subtitle={summary || undefined}
      // Плотная карточка: снятые с рамки пиксели уходят в строки таблицы.
      dense
      // Часы живут в ЛИСТЕ — правило hooks/useNow.ts. Держи их здесь, и тик раз в
      // пять секунд перерисовывал бы таблицу на сотню строк ради одной подписи.
      //
      // Переключатель режима — ЗДЕСЬ, а не в шапке таблицы рядом с окном
      // статистики, и это правка по живой проверке. В шапке он был симметричен
      // окну, но исчезал вместе с таблицей: у рынка, где мы ещё ни с кем не
      // торговали, строк ноль, таблицы нет — и вернуться в «весь рынок» оператору
      // было нечем, кроме перезагрузки страницы. Разница с окном статистики
      // содержательная: то сужает ЧЕТЫРЕ КОЛОНКИ, а это — состав строк.
      actions={
        <span className="flex items-baseline gap-3">
          {/* Переключатель разбивки — ЗДЕСЬ по тому же доводу, что и режим:
              внутри тела он исчезал бы вместе с таблицей на рынке без сделок, и
              вернуться было бы нечем. */}
          <ViewPicker view={view} onPick={setView} />
          {/* Фильтр по пометкам — ЗДЕСЬ, рядом с разбивкой и режимом, и по тому
              же доводу: внутри тела он исчезал бы вместе с таблицей на рынке, где
              фильтр не оставил ни одной строки, — то есть снять его было бы
              нечем, кроме перезагрузки страницы. */}
          <TagFilter />
          <ModePicker
            against={against}
            shown={p?.against}
            onPick={setAgainst}
          />
          {/* Запрос в пути — говорим об этом. Без пометки обновление панели
              невидимо: числа те же до самой подмены, и оператор не отличает
              «данные такие» от «данные едут». Особенно после перехода на другой
              рынок, где показывать пока нечего вовсе. */}
          {/* Пометка стоит в шапке ПОСТОЯННО и лишь прячется через visibility:
              шапка прижата к правому краю, и появляющееся слово сдвигало бы все
              кнопки левее на свою ширину при каждом запросе — раз в секунду. */}
          <span
            className={`text-xs text-[var(--color-muted)]${q.isFetching ? "" : " invisible"}`}
            title="запрос свода в пути"
            aria-hidden={!q.isFetching}
          >
            обновляем…
          </span>
          <DataAge p={p} />
        </span>
      }
    >
      {warnings.length > 0 && (
        <div className="mb-3 space-y-1">
          {warnings.map((w) => (
            // Словами, а не пустым местом: пустота на месте таблицы читается как
            // «сделок не было», а это другой факт.
            <p key={w} className="text-xs text-amber-300">
              {w}
            </p>
          ))}
        </div>
      )}

      {/* Отказ запроса называется словами. До этой правки его не было видно
          вовсе: при 502 от эндпоинта на экране стояло вечное «загрузка…», и
          оператор ждал данных, которых уже не будет. Со сводкой цена выросла —
          это первое, за чем в панель идут. */}
      {q.error && !p && (
        <p className="mb-3 text-sm text-[var(--color-sell)]">
          {(q.error as Error).message}
        </p>
      )}

      {/* Фильтр СУЖАЕТ таблицу до меньшинства рынка, а выглядит она при этом
          ровно как полный состав. Строка стоит НАД таблицей и жёлтым — вместе с
          прочими оговорками честности, а не под ней среди приписок: прочитать её
          надо раньше чисел, а не после. Она же остаётся единственным следом
          фильтра, когда тот не оставил ни одной строки. */}
      {filterNote && (
        <p className="mb-2 text-xs text-amber-300">{filterNote}</p>
      )}
      {p?.tags_reason && (
        <p className="mb-2 text-xs text-amber-300">{p.tags_reason}</p>
      )}
      {/* Нажатый фильтр по подгруппам, который применить нечем (режим «против
          нас», отказ базы), обязан объясниться: молча непринятый фильтр
          выглядит поломкой, а таблица под ним — полным составом рынка. */}
      {p?.groups_reason && (
        <p className="mb-2 text-xs text-amber-300">{p.groups_reason}</p>
      )}

      {lines.length === 0 ? (
        p?.known ? (
          // Текст зависит от режима: пустота значит «рынок молчит» либо «мы здесь
          // ни с кем не торговали», и это разные факты — см. emptyNote. Под
          // фильтром ответ третий, и подставлять вместо него первые два нельзя:
          // «никто не торговал» о рынке, где торговали сотни, — это ложь.
          <Empty>
            {/* Фильтр виноват только тогда, когда в окне ВООБЩЕ были кошельки.
                На молчащем рынке под включённым фильтром «фильтр не оставил
                строк» назвало бы причиной пометки, тогда как торгов не было
                вовсе, — и оператор снимал бы фильтр, ничего этим не изменив. */}
            {filterNote && (p.wallets_total ?? 0) > 0
              ? "фильтр по пометкам не оставил ни одной строки"
              : emptyNote(p)}
          </Empty>
        ) : (
          !warnings.length && !q.error && <Empty>загрузка…</Empty>
        )
      ) : (
        <>
          {/* Потолок высоты живёт на ОБЩЕЙ обёртке, а не на скроллере таблицы:
              так у карточки одна прокрутка, какую разбивку ни выбери, и высота
              ряда сетки не скачет при переключении. Вычитаемое 11rem не тронуто
              — оно про шапку и приписки.

              overflow-hidden здесь не косметика, а починка по замеру: обёртка с
              потолком, но без него, печатала вылезшее содержимое ПОВЕРХ приписок
              под карточкой — сводка на 855 кошельков не помещалась в 60vh, и её
              последний абзац налезал на flowNote. Клипует всегда, а прокручивает
              тот блок, который сейчас показан.

              В режиме «группы» потолка НЕТ, и это решение оператора (2026-09-01):
              сводка ограничена восемью подгруппами по два исхода, то есть
              высота у неё конечна и известна, а внутренняя прокрутка прятала
              нижние подгруппы и приписку. Таблица кошельков — сто строк, ей
              потолок остаётся.

              Полтора остатка экрана, а не остаток, — решение оператора
              (2026-09-02). Липкая полоса съедает 409 пикселей из тысячи, и
              «сколько осталось под ней» давало таблице 415 пикселей, то есть
              пятнадцать строк из ста: панель, заведённая ради вопроса «кто
              торгует», отвечала верхушкой верхушки. Цена честно названа: таблица
              теперь ниже сгиба, и до её конца доходят прокруткой страницы, а
              потом её собственной. Множитель стоит на ОБЕИХ ветках, чтобы узкий
              экран и широкий не расходились в поведении. */}
          <div
            className={
              byCohorts
                ? "flex min-h-0 flex-col"
                : "flex max-h-[90vh] min-h-0 flex-col overflow-hidden xl:max-h-[max(24rem,calc((100dvh-var(--pm-stack-top,0px)-11rem)*1.5))]"
            }
          >
            {/* Причина, по которой сводки нет, называется словами и таблицу не
                прячет: см. byCohorts выше. */}
            {view === "cohorts" && !byCohorts && cohorts.absent && (
              <p className="mb-2 shrink-0 text-xs text-[var(--color-muted)]">
                {cohorts.absent}
              </p>
            )}

            {byCohorts && (
              // Без собственной прокрутки: обёртка в этом режиме не ограничена
              // по высоте (см. выше), а вбок таблица сводки растягивается под
              // ширину карточки и в прокрутке не нуждается.
              <div className="min-h-0 flex-1">
                <CohortsBlock view={cohorts} onPick={pickFloor} />
              </div>
            )}

            {!byCohorts && (
            /* Горизонтальная прокрутка — ради режима «против нас»: колонок там
               пятнадцать, и на узком окне таблица иначе распирала бы страницу.
               Довод и пол в 16rem те же, что у ленты (TapePanel): панели стоят
               в ОДНОМ ряду сетки, и разные потолки означали бы, что высоту ряда
               задаёт большая из них, а меньшая дорисовывает под собой пустоту. */
            <div className="min-h-0 flex-1 overflow-y-auto overflow-x-auto">
            <table className="w-full text-xs">
              {/* z-10 и подложка НА ЯЧЕЙКАХ — починка по замеру 2026-09-02, и
                  ни то, ни другое не косметика.

                  z-[1]: чипы в колонке «Теги» сидят в контейнере с `relative`
                  (он нужен окошку правки), а позиционированное содержимое
                  рисуется ПОВЕРХ непозиционированного и в порядке дерева —
                  tbody идёт после thead. Прилипшая шапка со `z-index: auto`
                  проигрывала эту очередь, и уехавшие под неё чипы печатались
                  прямо по её тексту. Именам это не грозило (их ячейка не
                  позиционирована), поэтому до появления системных чипов в
                  каждой строке беда не показывалась.

                  РОВНО единица, а не десятка: этажом выше стои́т липкая полоса
                  страницы (MarketPage, z-10), и на равном z-index спор решает
                  порядок в дереве — таблица идёт позже, то есть её шапка
                  печаталась по кнопкам покупки. Порядок этажей теперь такой:
                  содержимое панели → её прилипшая шапка (1) → липкая полоса
                  страницы (10) → раскрытые окошки фильтра и пометок (20/30).
                  Единицы хватает: соперник у шапки один — позиционированные
                  чипы внутри её же таблицы, а у них z-index auto.

                  Подложка на `th`, а не только на `thead`: фон секции таблицы
                  прилипанием НЕ УЕЗЖАЕТ — он остаётся на месте, а ячейки
                  уходят вверх, и шапка оказывается прозрачной ровно тогда,
                  когда под ней что-то есть. Фон ячейки такого различия не
                  знает. */}
              <thead className="sticky top-0 z-[1] bg-[var(--color-panel)]">
                {/* Группирующая строка — не украшение. Восемь колонок описывают
                    ЭТО окно, пять последних — кошелька вообще, и без
                    разделителя «сделок 12» и «ставок 412» в одной строке
                    выглядят противоречием, а не двумя ответами. */}
                <tr className="text-left text-[var(--color-muted)] [&>th]:bg-[var(--color-panel)]">
                  {/* +2 к охвату: галочка отбора и колонка пометок. Обе описывают
                      КОШЕЛЬКА, а не окно, но стоят слева от разделителя, потому
                      что по ним таблицу читают сверху вниз вместе с именем. */}
                  <th className="pb-1 font-normal" colSpan={against ? 13 : 10}>
                    <span className="flex items-baseline gap-2">
                      <span className="text-[10px] uppercase tracking-wide">
                        {against ? "наши встречи в этом окне" : "в этом окне"}
                      </span>
                      {/* Чем окно погашено — либо почему колонки «Итог» нет.
                          ВИДИМОЙ строкой, а не подсказкой: сто прочерков подряд
                          читаются как «не посчитали», и ровно так их и прочитали
                          при первом показе. Стоит здесь, а не под таблицей,
                          потому что объясняет одну колонку, и объяснение обязано
                          быть над ней. */}
                      {settled.text !== "" && (
                        <span className="text-[10px] normal-case" title={settled.title}>
                          {settled.text}
                        </span>
                      )}
                    </span>
                  </th>
                  <th
                    className="border-l border-[var(--color-border)] pb-1 pl-3 font-normal"
                    colSpan={5}
                    title={
                      "статистика кошелька на КОРОТКИХ рынках, а не в этом окне: " +
                      "считается по его собственной истории сделок за выбранное окно — " +
                      "и только по тем рынкам, которые успели разрешиться"
                    }
                  >
                    {/* Окно — в шапке той группы, к которой относится, а не в углу
                        панели: переключатель, стоящий над всей таблицей, читался бы
                        как фильтр рынка, тогда как левые восемь колонок от него не
                        зависят вовсе. Подпись берётся из ОТВЕТА (shown), а
                        подсветка — из выбора (picked): пока свод едет, кнопка уже
                        нажата, а числа ещё прежние. */}
                    <span className="flex items-baseline gap-2">
                      <span className="text-[10px] uppercase tracking-wide">
                        на коротких рынках
                      </span>
                      <FlowWindowPicker
                        picked={picked}
                        shown={shown}
                        onPick={pick}
                      />
                    </span>
                  </th>
                </tr>
                <tr className="text-left text-[var(--color-muted)] [&>th]:bg-[var(--color-panel)]">
                  {/* Галочка «все показанные». Не «переключить каждую»: на
                      наполовину отмеченной таблице переключение дало бы инверсию,
                      а нажимают эту кнопку ради определённого состояния. */}
                  <th className="w-[1%] pb-2 pr-2 font-normal">
                    <PickAll lines={lines} />
                  </th>
                  {/* Имя и адрес — ОДНА колонка: это один ответ на один вопрос,
                      и рядом они стояли избыточно (см. WalletLabel). Адрес
                      показывается там, где имени нет, и остаётся в подсказке
                      всегда. */}
                  <th
                    className="pb-2 font-normal"
                    title={
                      "как кошелёк подписан на Polymarket: имя профиля либо выданный " +
                      "биржей псевдоним. У неподписанных — адрес; адрес целиком есть " +
                      "в подсказке любой строки. Ссылка ведёт на нашу страницу кошелька, " +
                      "«PM ↗» рядом — на его профиль на бирже"
                    }
                  >
                    Имя
                  </th>
                  {/* Пометки — СВОЯ колонка, а не приписка после имени: чипы
                      разной ширины, и приписанные к имени они вставали бы на
                      разном отступе, то есть просмотреть их глазом по вертикали
                      было бы нельзя. А смотрят на них именно так — ради «кто из
                      моих здесь». */}
                  <th
                    className="pb-2 font-normal"
                    title={
                      "пометки кошелька: тир и подгруппа сводки, выведенные обходами, " +
                      "и РУЧНЫЕ пометки оператора — их не выводит ничто, их ставят " +
                      "руками. Нажатие на «+» ставит пометку, нажатие на чип — " +
                      "фильтрует по ней; фильтр целиком — кнопкой «теги» в шапке карточки"
                    }
                  >
                    Теги
                  </th>
                  <th className="pb-2 font-normal">Исход</th>
                  <th
                    className="pb-2 text-right font-normal"
                    title="накопленное направление по модулю: знак вынесен в колонку исхода, по нему же идёт сортировка"
                  >
                    <Sort k="net" sort={sort} onSort={toggleSort}>
                      Нетто
                    </Sort>
                  </th>
                  {/* Две колонки роли — то, ради чего панель и заведена. Пустые
                      при обрезанной выборке: там роль неверна, а не приблизительна. */}
                  <th
                    className="pb-2 text-right font-normal"
                    title="набрано АГРЕССИЕЙ: кошелёк снимал заявки из стакана"
                  >
                    <Sort k="taker" sort={sort} onSort={toggleSort}>
                      Тейкером
                    </Sort>
                  </th>
                  <th
                    className="pb-2 text-right font-normal"
                    title="набрано СТОЯНИЕМ В СТАКАНЕ: кошелька снимали. Отрицательное значение — раздавал"
                  >
                    <Sort k="maker" sort={sort} onSort={toggleSort}>
                      Мейкером
                    </Sort>
                  </th>
                  <th
                    className="pb-2 text-right font-normal"
                    title="средняя цена ПОКУПОК токена показанного направления"
                  >
                    <Sort k="avg" sort={sort} onSort={toggleSort}>
                      Ср. цена
                    </Sort>
                  </th>
                  <th
                    className="pb-2 text-right font-normal"
                    title={
                      "число сделок кошелька в ЭТОМ окне. Нажатие раскрывает их " +
                      "список: когда зашёл, агрессией или стоянием, доливал или " +
                      "раздавал — то, чего одно число не говорит"
                    }
                  >
                    <Sort k="trades" sort={sort} onSort={toggleSort}>
                      Сделок
                    </Sort>
                  </th>
                  {/* Итог ЭТОГО окна — в группе «в этом окне», а не рядом с пнл
                      справа. Место значимо: справа стоит свод кошелька по ВСЕМ
                      коротким рынкам за выбранные сутки, и два знаковых числа в
                      долларах по соседству спутать было бы делом одного взгляда. */}
                  <th
                    className="pb-2 text-right font-normal"
                    title={
                      "итог кошелька в ЭТОМ окне по его погашению: продажи минус " +
                      "покупки плюс погашение победившего токена, за вычетом " +
                      "тейкерской комиссии. Прочерк означает «не знаем», а не ноль — " +
                      "причина сказана под таблицей"
                    }
                  >
                    <Sort k="settled" sort={sort} onSort={toggleSort}>
                      Итог
                    </Sort>
                  </th>
                  {/* Три колонки режима «против нас». Появляются только в нём:
                      вне режима они были бы прочерком в каждой строке. */}
                  {against && (
                    <>
                      <th
                        className="pb-2 text-right font-normal"
                        title={
                          "сколько шеров кошелёк взял ИМЕННО У НАС в этом рынке, обе роли вместе, " +
                          "и какую долю его оборота здесь это составило. Подсказка на числе " +
                          "разбирает роли: принял наш налив против снял нас"
                        }
                      >
                        <Sort k="took" sort={sort} onSort={toggleSort}>
                          У нас взял
                        </Sort>
                        {/* Доля — отдельная сортировка, потому что это отдельный
                            вопрос. Тысяча из пяти тысяч его оборота и тысяча из
                            тысячи — губка и держатель нашего мешка, и наверх их
                            выносят разные ключи. */}
                        <span className="ml-1 text-[10px]">
                          <Sort k="tookShare" sort={sort} onSort={toggleSort}>
                            ·%
                          </Sort>
                        </span>
                      </th>
                      <th
                        className="pb-2 text-right font-normal"
                        title={
                          "что он набрал ПОСЛЕ последней встречи с нами — мера сопротивления. " +
                          "Добор тейкером означает «пришёл защищать позицию, цену подвинет он»; " +
                          "«не приходил» — против такого можно спокойно стоять до экспирации. " +
                          "Прочерк значит другое: обход свода про эту встречу ещё не знает"
                        }
                      >
                        <Sort k="after" sort={sort} onSort={toggleSort}>
                          После нас
                        </Sort>
                      </th>
                      <th
                        className="pb-2 text-right font-normal"
                        title={
                          "куда ушла цена НАШЕГО токена с момента встречи, в центах и в нашу " +
                          "систему знаков: минус — против нас. Величина рыночная: цену двигал " +
                          "весь рынок, а не этот кошелёк"
                        }
                      >
                        <Sort k="priceMove" sort={sort} onSort={toggleSort}>
                          Цена
                        </Sort>
                      </th>
                    </>
                  )}
                  {/* Пять колонок про кошелька, а не про окно. Прочерк в них
                      означает «не обходили» — оборот ниже порога либо очередь не
                      дошла, — и это не то же самое, что «наторговал ноль». */}
                  <th
                    className="border-l border-[var(--color-border)] pb-2 pl-3 text-right font-normal"
                    title="сколько сделок кошелёк совершил на коротких рынках за глубину наблюдения"
                  >
                    <Sort k="bets" sort={sort} onSort={toggleSort}>
                      Ставок
                    </Sort>
                  </th>
                  <th
                    className="pb-2 text-right font-normal"
                    title={
                      "сколько кошелёк играет НА ОДНОМ ОКНЕ: оборот (покупки плюс продажи), " +
                      "делённый на число рынков. Не на число сделок — дробление входа на сотню " +
                      "мелких филлов это техника исполнения, а не размер игры. По этому же " +
                      "числу трейдер собирает реестр китов (FOLLOW_MIN_AVG_USD)"
                    }
                  >
                    <Sort k="avgBet" sort={sort} onSort={toggleSort}>
                      Ср. на окно
                    </Sort>
                  </th>
                  <th
                    className="pb-2 text-right font-normal"
                    title={
                      "прибыль на коротких рынках: продажи минус покупки плюс погашение " +
                      "победившего токена, ЗА ВЫЧЕТОМ тейкерской комиссии. На профиле " +
                      "Polymarket то же число будет больше ровно на неё — биржа комиссию " +
                      "из прибыли не вычитает. Считается по ордербук-потоку разрешившихся " +
                      "окон; минт и мерж пары сделками не являются и в источник не попадают"
                    }
                  >
                    <Sort k="pnl" sort={sort} onSort={toggleSort}>
                      пнл
                    </Sort>
                  </th>
                  <th
                    className="pb-2 text-right font-normal"
                    title="РОЙ: прибыль на доллар, занесённый в рынок покупками"
                  >
                    <Sort k="roi" sort={sort} onSort={toggleSort}>
                      РОЙ
                    </Sort>
                  </th>
                  {/* Выплаты стоят РЯДОМ с пнл и не складываются с ним. Замер
                      2026-08-18: биржа возвращает 64 % всей комиссии рынка, и с
                      учётом возврата «чистые агрессоры» за неделю не −$197 тыс.,
                      а +$30.7 тыс. Сложи два числа в одну колонку — и вопрос «на
                      чём этот кошелёк живёт» перестанет иметь ответ. */}
                  <th
                    className="pb-2 text-right font-normal"
                    title={
                      "сколько кошельку ДОПЛАТИЛИ помимо сделок за то же окно: возврат " +
                      "комиссии тейкеру и мейкеру, награды. Это НЕ входит в пнл слева — " +
                      "величины разные, и кошелёк, живущий на возврате, по одному пнл " +
                      "выглядит убыточным. Прочерк — выплат не видели: не спрашивали " +
                      "либо переводов за окно не было"
                    }
                  >
                    Выплаты
                  </th>
                </tr>
                {/* Итог — ТРЕТЬЕЙ строкой шапки, а не первой строкой тела, и это
                    не формальность: `sticky` стоит на всём `thead`, поэтому итог
                    остаётся на экране, докуда ни долистай таблицу. Положи его в
                    tbody — и он уехал бы вверх ровно тогда, когда нужен: при
                    сравнении суммы группы с дальними строками. */}
                {totals && (
                  <TotalsRow
                    totals={totals}
                    against={against}
                    holdersOpen={holdersOpen}
                    onToggleHolders={() => setHoldersOpen((v) => !v)}
                  />
                )}
              </thead>
              <tbody>
                {/* Подпункт итога: кто из сложенных кошельков ДЕРЖИТ шеры сейчас.
                    Первой строкой тела, сразу под итогом, потому что отвечает на
                    его продолжение: «набрали столько-то — а что у них на руках».
                    Балансы и накопление — разные величины (см. lib/holders). */}
                {totals && holdersOpen && (
                  <PickedHoldersRow
                    p={p}
                    tokens={tokens}
                    totals={totals}
                    tiers={tiers}
                    colSpan={against ? 18 : 15}
                  />
                )}
                {lines.map((line) => {
                  const open = openTrades.has(line.wallet);
                  return (
                    <Fragment key={line.wallet}>
                      <ParticipantTableRow
                        line={line}
                        tiers={tiers}
                        against={against}
                        expanded={open}
                        onToggleTrades={() => toggleTrades(line.wallet)}
                      />
                      {/* Раскрытый список — ОТДЕЛЬНАЯ строка под кошельком, а не
                          модальное окно: так видно накопление рядом, и раскрыть
                          можно сразу несколько кошельков, чтобы сравнить. */}
                      {open && (
                        <WalletTradesRow
                          conditionId={conditionId}
                          wallet={line.wallet}
                          tokens={tokens}
                          windowStart={windowStart}
                          colSpan={against ? 18 : 15}
                        />
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
            </div>
            )}
          </div>

          {/* Обе приписки — про таблицу кошельков: одна про её отсечку, вторая
              про её правые колонки. Под сводкой они описывали бы то, чего на
              экране нет, а у сводки приписка своя.

              В ОДНОЙ строке через точку, а не двумя абзацами: вместе они
              занимали под таблицей до четырёх строк — четыре строки кошельков,
              отнятых у панели навсегда, ради текста, который читают один раз за
              смену. Разбор обеих уехал в подсказки (см. hiddenNote и flowNote):
              не сокращён, а перестал стоять на экране постоянно. */}
          {!byCohorts && (
            <p className="mt-2 flex flex-wrap items-baseline gap-x-2 text-xs text-[var(--color-muted)]">
              {hidden.text && (
                <span title={hidden.title}>{hidden.text} ·</span>
              )}
              <span title={flow.title}>{flow.text}</span>
            </p>
          )}

          <HoldersBlock p={q.data} tokens={tokens} tiers={tiers} />
        </>
      )}
    </Panel>
  );
}

/**
 * Сводка по группам кошельков — над таблицей, внутри той же карточки.
 *
 * # Почему здесь, а не отдельной панелью
 *
 * Сводка и таблица — одни и те же величины над одним и тем же снимком. Разъедься
 * они по карточкам, разъехались бы и по возрасту в глазах читателя, а на странице
 * рынка вторая карточка вставала бы в РАЗНОЕ место в двух раскладках (со
 * свёрнутыми стаканами панель занимает их колонки). Оператор читает сводку и
 * таблицу одним движением: «плюсовые взяли Up — кто именно?»
 *
 * Не свёрнутой секцией, как держатели: те справочные, а это то, ради чего панель
 * открывают. Свёрнутая по умолчанию, она не показалась бы никогда.
 *
 * Вся арифметика и все тексты — в lib/cohorts.ts; здесь только разметка.
 */
function CohortsBlock({
  view,
  onPick,
}: {
  view: CohortsView;
  onPick: (usd: string) => void;
}) {
  // Пусто и молча — это ответ сервера до вехи: отсутствие функции не факт о
  // рынке. Причину, когда она есть (режим «против нас», отказ базы, выключенная
  // настройка), печатает панель — там же, где решает, чем занять карточку.
  if (view.lines.length === 0) return null;

  return (
    <div>
      <div className="mb-1 flex flex-wrap items-baseline gap-3">
        <span className="text-[10px] uppercase tracking-wide text-[var(--color-muted)]">
          Группы кошельков
        </span>
        <CohortFloorPicker
          floors={view.floors}
          shown={view.floor}
          onPick={onPick}
        />
      </div>

      {/* Основание стоит ДО чисел, а не после: «34 из 248» надо прочитать
          раньше, чем «+1 480 Up». Половина кошельков рынка не обойдена, и без
          основания сводка читается как утверждение о рынке, тогда как она
          утверждение о меньшинстве. */}
      {view.basis.map((line) => (
        <p key={line} className="text-xs text-[var(--color-muted)]">
          {line}
        </p>
      ))}
      {view.paid.text && (
        <p
          className={`text-xs ${
            view.paid.alarm ? "text-amber-300" : "text-[var(--color-muted)]"
          }`}
        >
          {view.paid.text}
        </p>
      )}
      {view.alarms.map((line) => (
        <p key={line} className="text-xs text-amber-300">
          {line}
        </p>
      ))}

      {/* Вбок таблица сводки едет прокруткой карточки: своего overflow здесь
          больше нет — вложенный скроллер внутри скроллера ловил бы колесо
          мыши на себя и не давал долистать блок до приписки. */}
      <div className="mt-2">
        <table className="w-full text-xs">
          <thead>
            {/* Та же грамматика, которой ниже разведены «в этом окне» и «на
                коротких рынках»: структура объявляет, что это ответы на РАЗНЫЕ
                вопросы, а не два измерения одного. */}
            <tr className="text-left text-[var(--color-muted)]">
              <th
                className="pb-1 font-normal"
                colSpan={5}
                title={
                  "вся НАКОПЛЕННАЯ глубина, а не вся история кошелька: сутки старше срока " +
                  "хранения убираются, а глубже пары суток за один обход не заглянуть"
                }
              >
                <span className="text-[10px] uppercase tracking-wide">
                  кто они — за всю накопленную глубину
                </span>
              </th>
              <th
                className="border-l border-[var(--color-border)] pb-1 pl-3 font-normal"
                colSpan={5}
                title={
                  "суммы по тем же кошелькам и тем же величинам, что в разбивке по кошелькам " +
                  "слева от разделителя. Переключатель «на коротких рынках» на эти числа не влияет. " +
                  "Под набранным мелко стоят деньги: куплено минус продано по исходу — именно из " +
                  "разности этих чисел и складывается «Перевес» справа"
                }
              >
                <span className="text-[10px] uppercase tracking-wide">
                  набрали в этом окне: шеры, под ними $
                </span>
              </th>
              <th
                className="border-l border-[var(--color-border)] pb-1 pl-3 text-right font-normal"
                title={view.lines[0]?.edgeHint}
              >
                <span className="text-[10px] uppercase tracking-wide">
                  куда смотрят, $
                </span>
              </th>
            </tr>
            {/* Ширины заданы намеренно. Первая колонка ограничена и переносит
                строки: разбор причин у «остальных» иначе растягивал её на
                полэкрана, отодвигая числа от имени. Четыре числовые колонки и
                перевес ужаты под содержимое (w-[1%] + nowrap), чтобы всё
                свободное место доставалось среднему блоку — там пять чисел в
                строке, и ему теснее всех. */}
            <tr className="text-left text-[var(--color-muted)]">
              <th className="w-[24ch] pb-1 font-normal">Кто</th>
              <th className="w-[1%] whitespace-nowrap pb-1 text-right font-normal">Кош.</th>
              <th
                className="w-[1%] whitespace-nowrap pb-1 text-right font-normal"
                title={view.lines[0]?.turnoverHint}
              >
                Оборот
              </th>
              <th className="w-[1%] whitespace-nowrap pb-1 text-right font-normal">Глубина</th>
              <th
                className="w-[1%] whitespace-nowrap pb-1 text-right font-normal"
                title={view.lines[0]?.winsHint}
              >
                Побед
              </th>
              <th className="border-l border-[var(--color-border)] pb-1 pl-3 font-normal">
                Токен
              </th>
              <th className="pb-1 text-right font-normal">Набрано</th>
              <th className="pb-1 text-right font-normal">Тейкером</th>
              <th className="pb-1 text-right font-normal">Мейкером</th>
              <th className="pb-1 text-right font-normal">Ср. цена</th>
              <th className="w-[1%] whitespace-nowrap border-l border-[var(--color-border)] pb-1 pl-3 text-right font-normal">
                Перевес
              </th>
            </tr>
          </thead>
          <tbody>
            {view.lines.map((line) => (
              <CohortRow key={line.key} line={line} />
            ))}
          </tbody>
        </table>
      </div>

      {/* Пустые подгруппы названы строкой, а не двумя строками прочерков
          каждая: ноль остаётся знанием, но не занимает место. */}
      {view.empty && (
        <p className="mt-1 text-xs text-[var(--color-muted)]">{view.empty}</p>
      )}

      {/* Приписка объясняет уже прочитанное: два окна, глубина, «набрано против
          перевеса». Текст собирается в lib — см. cohortsView. */}
      <p className="mt-2 text-xs text-[var(--color-muted)]">{view.note}</p>
    </div>
  );
}

/** Строка сводки: группа × токен. Левые ячейки и перевес — только у первой. */
function CohortRow({ line }: { line: CohortLine }) {
  return (
    <tr className="border-t border-[var(--color-border)] align-top">
      {line.head && (
        <>
          <td className="w-[24ch] py-1 pr-3" rowSpan={line.span}>
            <span title={line.titleHint}>{line.title}</span>
            {/* Определение печатается ПОД именем, а не прячется в подсказке:
                рядом на экране висят карточки сигналов со своим определением
                класса кита (отношение к вложенному, ±10 %, без выплат), и слово
                без формулы читалось бы как то же самое. */}
            <span className="block text-[10px] text-[var(--color-muted)]">
              {line.formula}
            </span>
            {line.roles && (
              <span
                className="block text-[10px] text-[var(--color-muted)]"
                title={line.rolesHint}
              >
                {line.roles}
              </span>
            )}
            {line.guessing && (
              <span
                className="block text-[10px] text-[var(--color-muted)]"
                title={line.guessingHint}
              >
                {line.guessing}
              </span>
            )}
            {/* Разбор причин — РАНЬШЕ оговорки про глубину: он отвечает, из
                кого группа состоит, а оговорка — насколько ей верить. */}
            {line.reasons && (
              <span className="block text-[10px] text-[var(--color-muted)]">
                {line.reasons}
              </span>
            )}
            {line.onePass && (
              <span
                className="block text-[10px] text-amber-300"
                title={line.onePassHint}
              >
                {line.onePass}
              </span>
            )}
          </td>
          {/* Ноль кошельков — ЗНАНИЕ («посмотрели, таких нет»), поэтому число, а
              не прочерк. Единственная ячейка блока с таким правилом. */}
          <td className="num whitespace-nowrap py-1 pr-3 text-right" rowSpan={line.span}>
            {line.wallets}
          </td>
          <td
            className="num whitespace-nowrap py-1 pr-3 text-right"
            rowSpan={line.span}
            title={line.turnoverHint}
          >
            {line.turnover ?? <Dash />}
          </td>
          <td
            className="num whitespace-nowrap py-1 pr-3 text-right"
            rowSpan={line.span}
            title={line.depthHint}
          >
            {line.depth ?? <Dash />}
          </td>
          {/* Прочерк, а не «0 %»: победы не читали либо архив окон не застал —
              ноль процентов означал бы «проиграли все окна». */}
          <td
            className="num whitespace-nowrap py-1 pr-3 text-right"
            rowSpan={line.span}
            title={line.winsHint}
          >
            {line.wins ?? <Dash />}
          </td>
        </>
      )}

      <td className="border-l border-[var(--color-border)] py-1 pl-3 pr-3">
        {/* Незнакомый исход — короткий токен, а НЕ прочерк: строк на группу две,
            и две строки, подписанные «—», это два числа без подписи. */}
        {line.outcome ? (
          <span style={{ color: outcomeColor(line.outcome) }}>{line.outcome}</span>
        ) : (
          <span
            className="num text-[var(--color-muted)]"
            title="имени исхода нет — справочник рынка не загружен"
          >
            {line.assetLabel}
          </span>
        )}
      </td>
      <td
        className="num py-1 pr-3 text-right"
        style={{ color: sharesColor(line.net) }}
      >
        {line.net ?? <Dash />}
        {/* Те же шеры в ДЕНЬГАХ, мелкой строкой под ними.
            Правая колонка «куда смотрят, $» до этого была числом ниоткуда: по
            шерам группа может выглядеть набравшей оба исхода, а перевеса не
            иметь вовсе. Перевес — это разность ЭТИХ двух чисел по исходам, и
            теперь её видно прямо в таблице. Мелко и приглушённо, потому что
            главная величина блока — всё-таки шеры: они отвечают на «что группа
            держит», а деньги — на «во что ей это встало». */}
        {line.netUsd && (
          <span
            className="block text-[10px] text-[var(--color-muted)]"
            title={
              "куплено минус продано по этому исходу, в долларах. Из разности таких " +
              "чисел по двум исходам и складывается «Перевес» справа"
            }
          >
            {line.netUsd}
          </span>
        )}
      </td>
      <td
        className="num py-1 pr-3 text-right"
        style={{ color: sharesColor(line.taker) }}
      >
        {line.taker ?? <Dash />}
      </td>
      <td
        className="num py-1 pr-3 text-right"
        style={{ color: sharesColor(line.maker) }}
      >
        {line.maker ?? <Dash />}
      </td>
      <td className="num py-1 text-right" title={line.avgHint}>
        {line.avg ?? <Dash />}
        {/* Вес средней стоит РЯДОМ, а не в подсказке: «+1 480 по 0.412» без него
            читается как «набрали позицию по 0.412», тогда как группа могла
            прокрутить вчетверо больше и войти в остаток по другой цене. */}
        {line.bought && (
          <span className="block text-[10px] text-[var(--color-muted)]">
            {line.bought}
          </span>
        )}
      </td>

      {line.head && (
        <td
          className="whitespace-nowrap border-l border-[var(--color-border)] py-1 pl-3 text-right"
          rowSpan={line.span}
          title={line.edgeHint}
        >
          {line.edge === null ? (
            <Dash />
          ) : (
            <>
              {/* Знак вынесен в ИМЯ ИСХОДА: «−$780» читалось бы как убыток. */}
              <span
                className="block"
                style={{ color: outcomeColor(line.edgeOutcome) }}
              >
                {line.edgeOutcome || "нет перевеса"}
              </span>
              <span className="num block">{line.edge}</span>
              <span className="block text-[10px] text-[var(--color-muted)]">
                {line.edgeSpread}
              </span>
            </>
          )}
        </td>
      )}
    </tr>
  );
}

/**
 * Чип подгруппы сводки в строке кошелька: «тейкер+», «выплаты», «мейкер−».
 *
 * Той же формой, что тир (класс `.tier`), но это ДРУГОЕ утверждение, и подсказка
 * говорит какое: тир — по нашим встречам, подгруппа — по всей истории кошелька
 * на коротких рынках при умолчальной ступени порога. Нет подгруппы — нет чипа:
 * «остальные» названы причиной под таблицей сводки, а пустой чип читался бы как
 * «ни то, ни другое».
 */
function CohortChip({ kind }: { kind: ParticipantCohortKind | undefined }) {
  const state = useParticipantTags((s) => s.groupState);
  const cycle = useParticipantTags((s) => s.cycleGroup);
  if (!kind || !SHORT_TITLES[kind]) return null;
  const st = state(kind);
  return (
    <button
      type="button"
      onClick={() => cycle(kind)}
      // Нажимается — как и ручной чип, и тем же кольцом: вопрос, который задают,
      // увидев подгруппу, ровно один — «а кто ещё такой?». Отвечает на него
      // фильтр, и держать его только в списке наверху значило бы прятать ответ
      // на два нажатия дальше.
      className={`tier hover:text-white ${
        st === "include"
          ? "bg-[var(--color-accent)]/20"
          : st === "exclude"
            ? "line-through opacity-60"
            : ""
      }`}
      style={{ color: signOf(kind) === "minus" ? "var(--color-sell)" : undefined }}
      title={
        `${TITLES[kind]} — подгруппа сводки при умолчальной ступени порога оборота, ` +
        "за всю накопленную глубину кошелька. Это не тир: тот считается по нашим " +
        "встречам. Нажатие включает фильтр по подгруппе: показать только таких, " +
        "потом убрать таких, потом снять"
      }
    >
      {SHORT_TITLES[kind]}
    </button>
  );
}

/**
 * Переключатель ступени порога оборота.
 *
 * Близнец FlowWindowPicker, но БЕЗ приписки «показано за …», и это не
 * упущение: все ступени приезжают одним ответом, порог в запрос не уходит
 * вовсе, поэтому выбранное всегда равно показанному. Расхождения, за которое
 * соседние два переключателя платят жёлтой строкой, здесь не существует.
 */
function CohortFloorPicker({
  floors,
  shown,
  onPick,
}: {
  floors: readonly CohortFloor[];
  shown: CohortFloor | undefined;
  onPick: (usd: string) => void;
}) {
  if (floors.length < 2) return null;
  return (
    <span className="flex items-baseline gap-1">
      <span className="text-[10px] text-[var(--color-muted)]">
        порог оборота:
      </span>
      {floors.map((f) => (
        <button
          key={f.usd}
          type="button"
          onClick={() => onPick(f.usd)}
          title={
            "кошельки с оборотом ниже этого порога за всю накопленную глубину не " +
            "классифицируются вовсе: знак у мелкого кошелька ничего не говорит о том, " +
            "как он торгует"
          }
          className={`rounded px-1 text-[10px] normal-case ${
            f.usd === shown?.usd
              ? "bg-[var(--color-accent)]/20 text-[var(--color-fg)]"
              : "text-[var(--color-muted)] hover:text-white"
          }`}
        >
          {f.label}
        </button>
      ))}
    </span>
  );
}

/** Прочерк: величины НЕТ. Отличается от нуля, который есть утверждение. */
function Dash() {
  return <span className="text-[var(--color-muted)]">—</span>;
}

/**
 * Заголовок, по которому таблица переставляется.
 *
 * # Почему щелчков три, а не два
 *
 * Вниз, вверх, обратно в порядок выдачи. Третье состояние здесь не роскошь:
 * порядок выдачи содержателен — по нему бэкенд решал, кто попадёт в отсечку, — и
 * без возврата к нему оператор, ткнувший в «пнл» на живом рынке, больше никогда
 * не увидел бы таблицу такой, какой её отобрал сервер.
 *
 * Возрастание нужно ровно так же, как убывание, и это не симметрия ради
 * симметрии: «кто больше всех взял у нас» и «кто ушёл в минус сильнее всех» —
 * два разных конца одной колонки.
 */
function Sort({
  k,
  sort,
  onSort,
  children,
}: {
  k: ParticipantSortKey;
  sort: ParticipantSort | undefined;
  onSort: (key: ParticipantSortKey) => void;
  children: React.ReactNode;
}) {
  const desc = sort && sort.key === k ? sort.desc : null;
  return (
    <button
      type="button"
      onClick={() => onSort(k)}
      className={`group font-normal ${
        desc === null ? "hover:text-white" : "text-[var(--color-fg)]"
      }`}
    >
      {children}
      {/* Стрелка занимает место всегда: иначе колонка прыгала бы на ширину
          символа при каждом щелчке — на таблице, где числа сравнивают глазом по
          вертикали. Пока колонка не выбрана, стрелка видна только под курсором:
          тринадцать значков в шапке спорили бы с самими числами. */}
      <span
        className={`ml-0.5 ${
          desc === null
            ? "opacity-0 group-hover:opacity-60"
            : "text-[var(--color-accent)]"
        }`}
      >
        {desc === null ? "↕" : desc ? "↓" : "↑"}
      </span>
    </button>
  );
}

/**
 * Возраст данных — и он же граница применимости всей таблицы.
 *
 * Отдельный компонент, потому что здесь тикают часы: возраст обязан расти сам,
 * иначе четырёхминутное отставание выглядит секундным до следующего ответа. Лист
 * дерева по правилу hooks/useNow.ts — перерисовывается одна подпись, а не таблица.
 */
function DataAge({ p }: { p: ParticipantsStats | undefined }) {
  const nowMs = useNow(5000);
  const age = ageLabel(p, nowMs);
  if (!age.text) return null;
  // Ширина зарезервирована под самую длинную ОБЫЧНУЮ подпись источника
  // («лента, 59 с», «индексатор, 4 мин 59 с»), а текст прижат вправо. Иначе
  // каждый переход «9 с» → «10 с» → «1 мин 5 с» менял ширину подписи, и все
  // кнопки шапки, прижатой к правому краю, скакали влево-вправо раз в тик.
  // Редкие более длинные формы («> 1 ч» на индексаторе) растягивают поле — это
  // одно движение за окно, а не пила.
  const feed = p?.source === "feed";
  return (
    <span
      className={`inline-block whitespace-nowrap text-right text-xs ${
        feed ? "min-w-[5.5rem]" : "min-w-[10.5rem]"
      } ${age.alarm ? "text-amber-300" : "text-[var(--color-muted)]"}`}
      title={
        p?.source === "feed"
          ? "источник — живая лента активности биржи, которую держит трейдер: " +
            "чужая сделка видна примерно за секунду. Число — возраст самой " +
            "свежей сделки, а не нашего запроса; десятки секунд на живом окне " +
            "означают, что лента замолчала" +
            (p.removed
              ? `. Не подтверждено расчётом и не учтено: ${p.removed} из ${p.fills_n ?? 0}`
              : "")
          : "источник — индексатор Polymarket: он отстаёт на минуты и отстаёт " +
            "РЫВКАМИ, поэтому на пятиминутном окне таблица описывает прошедшее " +
            "окно, а не идущее. Число — возраст самой свежей проиндексированной " +
            "сделки, а не нашего запроса"
      }
    >
      {age.text}
    </span>
  );
}

function ParticipantTableRow({
  line,
  tiers,
  against,
  expanded,
  onToggleTrades,
}: {
  line: ParticipantLine;
  tiers: Readonly<Record<string, WalletTier>>;
  against: boolean;
  /** Список сделок этого кошелька раскрыт. */
  expanded: boolean;
  onToggleTrades: () => void;
}) {
  return (
    <tr
      className={`border-t border-[var(--color-border)]${
        // Наш кошелёк подсвечен: «где в этом списке я» — первый вопрос к такой
        // таблице, и искать свой адрес глазами оператор не должен.
        line.ours ? " bg-[var(--color-accent)]/10" : ""
      }`}
    >
      {/* Отметка кошелька в группу. Первой колонкой и узкой: её нажимают
          движением сверху вниз по списку, и любой отступ слева ломает это
          движение. */}
      <td className="w-[1%] py-1 pr-2">
        <PickBox wallet={line.wallet} />
      </td>
      {/* Имя, а у неподписанных — адрес: одна колонка вместо двух (см.
          WalletLabel). Обычным цветом, а не приглушённым: приглушённое читается
          как второстепенное, а это ровно то, за что цепляется глаз. */}
      <td className="max-w-[18ch] py-1 pr-3">
        <span className="inline-flex max-w-full items-center gap-1.5">
          <WalletLabel address={line.wallet} name={line.name} />
          {line.ours && <span className="shrink-0 text-[var(--color-accent)]">мы</span>}
        </span>
      </td>
      {/* Все пометки кошелька — ОДНОЙ ячейкой: выведенные системой (тир и
          подгруппа) и произнесённые человеком. Смотрят на них одинаково — «кто
          из моих здесь», — и разводить их по колонкам значило бы заставить глаз
          собирать одну мысль из двух мест. Разница между ними никуда не делась и
          живёт в подсказках чипов: тир и подгруппу переписывают обходы, ручную
          пометку не выводит ничто.

          Порядок постоянен: сперва тир, потом подгруппа, потом ручные. Чипы
          читают столбиком, и плавающий порядок сделал бы это невозможным.

          Два источника тира, и порядок между ними значим: горячий поток
          (profile_update) свежее, но приходит ТОЛЬКО при смене тира, поэтому
          основание — поле строки из ответа. Тира нет вовсе — значка нет:
          участник рынка мог с нами ни разу не торговать, а тир считается по
          нашим встречам. */}
      <td className="py-1 pr-3">
        <TagCell
          wallet={line.wallet}
          tags={line.tags}
          tier={tiers[line.wallet.toLowerCase()] ?? line.tier}
          profile={line.profile}
        />
      </td>
      <td className="py-1 pr-3" style={{ color: outcomeColor(line.outcome) }}>
        {line.outcome || <span className="text-[var(--color-muted)]">—</span>}
      </td>
      <td className="num py-1 pr-3 text-right">{line.net}</td>
      <td className="num py-1 pr-3 text-right">{signed(line.taker)}</td>
      <td className="num py-1 pr-3 text-right">{signed(line.maker)}</td>
      <td className="num py-1 pr-3 text-right text-[var(--color-muted)]">
        {line.avg || "—"}
      </td>
      {/* Число сделок — КНОПКА. Одно число отвечает «сколько», а вопрос
          оператора звучит иначе: КАК он это набрал. Треугольник тот же, что у
          свёрнутых держателей ниже в этой же панели. Строк без сделок кнопкой не
          делаем: раскрывать там нечего, а мёртвая кнопка учит не нажимать. */}
      <td className="num py-1 pr-3 text-right text-[var(--color-muted)]">
        {line.trades > 0 ? (
          <button
            type="button"
            onClick={onToggleTrades}
            className="hover:text-white"
            title="показать сделки этого кошелька в окне: время, роль, размер и цену"
            aria-expanded={expanded}
          >
            {line.trades} {expanded ? "▾" : "▸"}
          </button>
        ) : (
          line.trades
        )}
      </td>
      <td
        className="num py-1 pr-3 text-right"
        style={{ color: pnlColor(line.settled) }}
        title={line.settledTitle}
      >
        {line.settled ?? <span className="text-[var(--color-muted)]">—</span>}
      </td>
      {against && <AgainstCells line={line} />}
      {/* Глубина наблюдения висит подсказкой на КАЖДОЙ из четырёх ячеек, а не
          отдельной колонкой: у нового кошелька за ними одни сутки, у знакомого —
          неделя, и прочитать первое как второе — ровно та ошибка, ради которой
          глубина и считается. Отдельная колонка на это не заслуживает места. */}
      <td
        className="num border-l border-[var(--color-border)] py-1 pl-3 pr-3 text-right"
        title={depthTitle(line)}
      >
        {line.bets ?? <span className="text-[var(--color-muted)]">—</span>}
      </td>
      <td className="num py-1 pr-3 text-right" title={depthTitle(line)}>
        {line.avgBet ?? <span className="text-[var(--color-muted)]">—</span>}
      </td>
      <td
        className="num py-1 pr-3 text-right"
        style={{ color: pnlColor(line.pnl) }}
        title={pnlTitle(line)}
      >
        {line.pnl ?? <span className="text-[var(--color-muted)]">—</span>}
        {/* Глубина — рядом с числом и ВИДИМАЯ. Разбор 2026-08-12: на экране
            стояло −$2 187 под шапкой «за 7 суток», а суток за этим числом было
            двое, и это были ровно два убыточных дня кошелька, прибыльного на
            всей своей глубине. Подсказку при наведении в такой ситуации никто не
            открывает — её открывают, уже заподозрив неладное. */}
        {line.depthLabel && (
          <span
            className="ml-1 text-[10px] text-[var(--color-muted)]"
            title={depthTitle(line)}
          >
            ·{line.depthLabel}
            {/* Звёздочка — сутки покрыты не целиком. Две причины и обе занижают:
                сегодняшние ещё идут, а у самых активных кошельков десять тысяч
                строк источника укладываются в четыре часа, то есть полные сутки
                недостижимы в принципе. */}
            {line.partial && "*"}
          </span>
        )}
      </td>
      <td
        className="num py-1 text-right"
        style={{ color: pnlColor(line.roi) }}
        title={depthTitle(line)}
      >
        {line.roi ?? <span className="text-[var(--color-muted)]">—</span>}
      </td>
      <td
        className="num py-1 pl-3 text-right"
        style={{ color: pnlColor(line.paid) }}
        title={line.paidTitle}
      >
        {line.paid ?? <span className="text-[var(--color-muted)]">—</span>}
      </td>
    </tr>
  );
}

/**
 * Раскрытый список сделок одного кошелька в окне.
 *
 * # Зачем он на экране
 *
 * Панель отвечает «сколько он набрал и какой ролью» — одним числом. Вопрос, на
 * который она не отвечает: КАК он это набрал. Зашёл на седьмой секунде или
 * дождался последней минуты; долил разом или размазал по сотне филлов; брал
 * агрессией или стоял в стакане; и не начал ли в середине окна раздавать то,
 * что набрал в начале. Это разные участники и разные выводы.
 *
 * # Почему строки по возрастанию времени
 *
 * Потому что читают их как рассказ, сверху вниз. Источник отдаёт от свежей к
 * старой, и порядок разворачивает сервер — там же, где размечает роль.
 *
 * Разметка здесь, правила — в lib/wallettrades.ts: компонентных тестов в
 * проекте нет.
 */
function WalletTradesRow({
  conditionId,
  wallet,
  tokens,
  windowStart,
  colSpan,
}: {
  conditionId: string;
  wallet: string;
  tokens: readonly Token[];
  /** Начало окна для колонки τ. null — колонка не рисуется. */
  windowStart: Date | null;
  colSpan: number;
}) {
  const q = useMarketWalletTrades(conditionId, wallet, true);
  const rows = walletTradeLines(q.data, tokens, windowStart);
  const note = walletTradesNote(q.data);

  return (
    <tr className="border-t border-[var(--color-border)] bg-[var(--color-bg)]/40">
      <td colSpan={colSpan} className="px-3 py-2">
        {q.isLoading && !q.data && (
          <p className="text-xs text-[var(--color-muted)]">загрузка сделок…</p>
        )}
        {q.error && (
          // Словами, а не пустотой: пустое место читается как «сделок не было»,
          // а это другой факт.
          <p className="text-xs text-[var(--color-sell)]">{(q.error as Error).message}</p>
        )}
        {q.data?.reason && (
          <p className="text-xs text-[var(--color-muted)]">{q.data.reason}</p>
        )}
        {rows.length > 0 && (
          // Своя прокрутка: у кошелька в плотном окне бывают десятки строк, а
          // раскрытая строка не должна вытеснять таблицу с экрана.
          <div className="max-h-56 overflow-y-auto">
            <table className="w-full text-[11px]">
              {/* Подложка на ЯЧЕЙКАХ по тому же доводу, что у таблицы кошельков
                  выше: фон секции при прилипании не уезжает, и шапка становится
                  прозрачной ровно тогда, когда под ней есть строки. */}
              <thead className="sticky top-0 z-[1] bg-[var(--color-panel)] text-left text-[var(--color-muted)]">
                <tr className="[&>th]:bg-[var(--color-panel)]">
                  <th className="pb-1 pr-3 font-normal">Время</th>
                  {windowStart && (
                    <th
                      className="pb-1 pr-3 text-right font-normal"
                      title="секунда от начала окна: зашёл сразу или дождался конца"
                    >
                      τ
                    </th>
                  )}
                  <th className="pb-1 pr-3 font-normal">Сторона</th>
                  <th className="pb-1 pr-3 font-normal">Исход</th>
                  <th
                    className="pb-1 pr-3 font-normal"
                    title={
                      "тейкер — снимал заявки, мейкер — стоял в стакане. Прочерк " +
                      "означает, что в этой транзакции кошелёк стоял несколькими " +
                      "ордерами и разложить их поимённо нечем"
                    }
                  >
                    Роль
                  </th>
                  <th className="pb-1 pr-3 text-right font-normal">Размер</th>
                  <th className="pb-1 pr-3 text-right font-normal">Цена</th>
                  <th className="pb-1 text-right font-normal">$</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.key} className="border-t border-[var(--color-border)]/50">
                    <td className="num py-0.5 pr-3">{r.time}</td>
                    {windowStart && (
                      <td className="num py-0.5 pr-3 text-right text-[var(--color-muted)]">
                        {r.tau}
                      </td>
                    )}
                    <td
                      className="py-0.5 pr-3"
                      style={{ color: r.side === "BUY" ? "var(--color-buy)" : "var(--color-sell)" }}
                    >
                      {r.side}
                    </td>
                    <td className="py-0.5 pr-3" style={{ color: outcomeColor(r.outcome) }}>
                      {r.outcome || <span className="text-[var(--color-muted)]">—</span>}
                    </td>
                    <td className="py-0.5 pr-3 text-[var(--color-muted)]">
                      {r.role || "—"}
                    </td>
                    <td className="num py-0.5 pr-3 text-right">{r.size}</td>
                    <td className="num py-0.5 pr-3 text-right">{r.price}</td>
                    <td className="num py-0.5 text-right text-[var(--color-muted)]">{r.usd}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {/* Приписка честности: обрезанная выдача и неразложенные роли. Пустое
            место на их месте выдавало бы неполное за полное. */}
        {note !== "" && <p className="mt-1 text-[11px] text-amber-300">{note}</p>}
      </td>
    </tr>
  );
}

/**
 * Три ячейки режима «против нас»: сколько взял, что сделал после, куда ушла цена.
 *
 * # Зачем этот режим на экране
 *
 * Оператор бьёт тейкером и не видит, КТО принял налив. Панель отвечала «кто набрал
 * в рынке», а вопрос звучит иначе: у кого теперь МОЙ мешок и придёт ли он его
 * отыгрывать. Первое читается из нашего журнала (он знает контрагента точно),
 * второе — из того же свода рынка, только по времени: что кошелёк добрал ПОСЛЕ
 * встречи и какой ролью.
 *
 * Практический вывод разный и противоположный: кто после налива в рынок не пришёл
 * — против того можно спокойно стоять до экспирации; кто тут же добрал агрессией —
 * тот тянет цену обратно, и переставляться надо быстро.
 */
function AgainstCells({ line }: { line: ParticipantLine }) {
  return (
    <>
      <td
        className="num border-l border-[var(--color-border)] py-1 pl-3 pr-3 text-right"
        title={line.tookTitle || undefined}
      >
        {line.took ?? <span className="text-[var(--color-muted)]">—</span>}
        {/* Доля — рядом с числом и мелким шрифтом. Она отвечает на второй вопрос
            той же ячейки: тысяча из его пяти тысяч и тысяча из его тысячи — это
            губка и держатель нашего мешка, и вести себя с ними надо по-разному. */}
        {line.tookShare && (
          <span className="ml-1 text-[10px] text-[var(--color-muted)]">
            ·{line.tookShare}
          </span>
        )}
      </td>
      <td className="num py-1 pr-3 text-right" title={afterTitle(line)}>
        {line.after ? (
          <span
            // «не приходил» — обычным цветом: это не тревога, а хорошая новость.
            // Добор агрессией выделен — он и есть сигнал переставляться.
            className={
              line.afterRole === "тейкером" ? "text-amber-300" : undefined
            }
          >
            {line.after}
            {line.afterRole && (
              <span className="ml-1 text-[10px] text-[var(--color-muted)]">
                {line.afterRole === "тейкером" ? "т" : "м"}
              </span>
            )}
          </span>
        ) : (
          <span className="text-[var(--color-muted)]">—</span>
        )}
      </td>
      <td
        className="num py-1 pr-3 text-right"
        style={{ color: pnlColor(line.priceMove) }}
        title={
          "куда ушла цена НАШЕГО токена с момента встречи. Минус означает «против нас»: " +
          "знак развёрнут по нашей стороне, поэтому читать его вместе с колонкой " +
          "направления не нужно"
        }
      >
        {line.priceMove ?? <span className="text-[var(--color-muted)]">—</span>}
      </td>
    </>
  );
}

/**
 * Подсказка колонки «после нас».
 *
 * Прочерк и «не приходил» — РАЗНЫЕ ответы, и различие обязано быть на экране:
 * первое означает, что свод про эту встречу ещё не знает (филл свежее снимка), а
 * второе — что кошелёк действительно не возвращался. Прочитать первое как второе
 * значило бы решить «он не защищается» по кошельку, о котором ничего не известно.
 */
function afterTitle(line: ParticipantLine): string {
  if (!line.after) {
    return (
      "свод рынка про эту встречу ещё не знает: филл свежее последнего обхода " +
      "(он идёт раз в 5–15 секунд). Это НЕ «кошелёк не приходил»"
    );
  }
  if (line.after === "не приходил") {
    return (
      "после встречи кошелёк в рынок не возвращался — позицию не защищает. " +
      "Против такого можно стоять до экспирации"
    );
  }
  const role =
    line.afterRole === "тейкером"
      ? "АГРЕССИЕЙ: снимал заявки из стакана, то есть двигал цену сам — " +
        "против такого надо переставляться быстро"
      : "стоянием в стакане: подставил заявку, но цену не двигал";
  return `после встречи с нами набрал ${line.after} share ${role}`;
}

/**
 * Подсказка о глубине наблюдения.
 *
 * Говорится словами, а не числом рядом с колонкой: «за 1 сут» и «за 7 сут» — это
 * разная степень доверия к тем же четырём цифрам, и у нового кошелька глубина
 * ровно одни сутки, потому что глубже история не влезает в потолок источника.
 */
function depthTitle(line: ParticipantLine): string {
  if (line.depthDays === 0) {
    return (
      "кошелька ещё не обходили: либо его оборот ниже порога наблюдения, " +
      "либо очередь до него не дошла. Это не «наторговал ноль»"
    );
  }
  const noun = plural(line.depthDays, "сутки", "суток", "суток");
  const base =
    `по ${line.depthDays} ${noun} наблюдений на коротких рынках — ` +
    "фактическая глубина свода, она бывает короче запрошенного окна: " +
    "за один обход глубже двух суток не заглянуть";
  const parts = [base];
  if (line.partial) {
    parts.push(
      "Часть суток покрыта не целиком — сегодняшние ещё идут либо история " +
        "кошелька плотнее одного обхода (у самых активных десять тысяч строк источника " +
        "укладываются в четыре часа). Числа за такие сутки занижены",
    );
  }
  // Непригодные сутки называются ОТДЕЛЬНО от неполных, потому что это разные
  // диагнозы: неполные занижены, а непригодные выброшены целиком — выборки
  // истории разошлись, и комиссия в них начислена на сделки, которых нет в
  // обороте. Молчать о них значило бы выдать сумму по пяти суткам за сумму по
  // семи.
  if (line.unusableDays > 0) {
    const noun = plural(line.unusableDays, "сутки", "суток", "суток");
    parts.push(
      `${line.unusableDays} ${noun} записаны прочерком: выборки истории ` +
        "разошлись, и пнл с ролью за них выброшены — в деньги свода они НЕ " +
        "вошли, а в ставки и оборот входят",
    );
  }
  return parts.join(". ");
}

/**
 * Разбор пнл: сколько было ДО комиссии, сколько съела комиссия, что осталось.
 *
 * # Зачем это в подсказке ячейки, а не только в приписке под таблицей
 *
 * Потому что вопрос задают о КОНКРЕТНОЙ строке. Замер 2026-08-12 по кошельку
 * `0x251c…541f`: у нас −$7 001, у Polymarket +$14 344 за те же трое суток, и обе
 * цифры верны — разница ровно комиссия, $20 739 за три дня при 14 892 агрессивных
 * сделках. Без разбора рядом с числом это выглядит ошибкой счёта, и проверять её
 * приходится вручную по сырым данным.
 *
 * Глубина остаётся в той же подсказке: второй вопрос к тому же числу — «а за
 * сколько это посчитано».
 */
function pnlTitle(line: ParticipantLine): string {
  const depth = depthTitle(line);
  if (!line.pnl || !line.pnlGross || !line.fee) return depth;
  return (
    `до комиссии ${line.pnlGross}, тейкерская комиссия ${line.fee}, итог ${line.pnl}. ` +
    `На профиле Polymarket за этот период будет ${line.pnlGross}: биржа комиссию ` +
    `из прибыли не вычитает.\n${depth}`
  );
}

/**
 * Переключатель разбивки: по кошелькам или по группам.
 *
 * Приписки «показано другое», которой платят соседние два переключателя, здесь
 * нет и быть не может: обе разбивки приезжают ОДНИМ ответом и считаются по
 * одному снимку, поэтому нажатое всегда равно показанному, а переключение не
 * стоит ни запроса, ни сброса возраста данных. Это выбор того, ЧТО показать, а
 * не того, что спросить, — как и ступень порога рядом.
 *
 * Почему на экране одна разбивка, а не обе сразу, разобрано там, где живёт
 * выбор: store/useParticipantsView.ts.
 */
function ViewPicker({
  view,
  onPick,
}: {
  view: ParticipantsView;
  onPick: (view: ParticipantsView) => void;
}) {
  return (
    <span className="flex items-baseline gap-1">
      {[
        {
          value: "wallets" as const,
          label: "кошельки",
          title:
            "по одному кошельку: крупнейшие по накоплению в этом окне — с ролью, " +
            "средней ценой и статистикой каждого на коротких рынках",
        },
        {
          value: "cohorts" as const,
          label: "группы",
          title:
            "сводка по ВСЕМ кошелькам рынка, а не по сотне показанных: плюсовые, " +
            "минусовые и остальные — сколько набрала каждая группа и по какой средней",
        },
      ].map((v) => (
        <button
          key={v.value}
          type="button"
          onClick={() => onPick(v.value)}
          title={v.title}
          className={`rounded px-1 text-[10px] normal-case ${
            v.value === view
              ? "bg-[var(--color-accent)]/20 text-[var(--color-fg)]"
              : "text-[var(--color-muted)] hover:text-white"
          }`}
        >
          {v.label}
        </button>
      ))}
    </span>
  );
}

/**
 * Переключатель режима: весь рынок или только наши контрагенты.
 *
 * `against` — что нажал оператор, `shown` — за что посчитаны строки, которые
 * сейчас на экране. Разводятся они по той же причине, что у окна статистики, и
 * цена ошибки здесь выше: между нажатием и ответом на экране стоит ПОДМНОЖЕСТВО
 * рынка либо, наоборот, весь рынок — и подписать чужие кошельки как своих
 * контрагентов значило бы соврать ровно в том поле, ради которого режим заведён.
 */
function ModePicker({
  against,
  shown,
  onPick,
}: {
  against: boolean;
  shown: boolean | undefined;
  onPick: (against: boolean) => void;
}) {
  return (
    <span className="flex items-baseline gap-1">
      {[
        {
          value: false,
          label: "весь рынок",
          title: "все, кто торговал в этом окне",
        },
        {
          value: true,
          label: "против нас",
          title:
            "только те, с кем торговали МЫ: кто принял наш налив и кто снял нас. " +
            "Плюс три колонки — сколько он взял у нас, что добрал после встречи и " +
            "куда с тех пор ушла цена",
        },
      ].map((m) => (
        <button
          key={String(m.value)}
          type="button"
          onClick={() => onPick(m.value)}
          title={m.title}
          className={`rounded px-1 text-[10px] normal-case ${
            m.value === against
              ? "bg-[var(--color-accent)]/20 text-[var(--color-fg)]"
              : "text-[var(--color-muted)] hover:text-white"
          }`}
        >
          {m.label}
        </button>
      ))}
      {/* Кнопка уже нажата, а строки ещё прежние — это видно словами, а не
          догадкой по мигнувшей таблице. */}
      {shown !== undefined && shown !== against && (
        <span className="text-[10px] normal-case text-amber-300">
          показан {shown ? "срез против нас" : "весь рынок"}…
        </span>
      )}
    </span>
  );
}

/**
 * Переключатель окна статистики.
 *
 * # Почему нажатая кнопка и подписанное окно — разные величины
 *
 * `picked` — что нажал оператор, `shown` — за что посчитаны числа, которые сейчас
 * на экране. Между ними живёт запрос: пока он едет, кнопка обязана быть нажатой
 * (иначе интерфейс выглядит сломанным), а числа остаются прежними — и подписаны
 * должны быть по-прежнему. Свести их в одно значило бы на полсекунды поставить
 * подпись «за 30 суток» над недельными числами, то есть сделать ровно ту ошибку,
 * ради которой переключатель и появился.
 *
 * Пустой `picked` — оператор не выбирал, окно назначил сервер; тогда нажатой
 * подсвечивается та кнопка, которую он назвал в ответе.
 */
function FlowWindowPicker({
  picked,
  shown,
  onPick,
}: {
  picked: FlowWindow | undefined;
  shown: FlowWindow | undefined;
  onPick: (window: FlowWindow) => void;
}) {
  const active = picked ?? shown;
  return (
    <span className="flex items-baseline gap-1">
      {FLOW_WINDOWS.map((w) => (
        <button
          key={w.value}
          type="button"
          onClick={() => onPick(w.value)}
          title={w.title}
          className={`rounded px-1 text-[10px] normal-case ${
            w.value === active
              ? "bg-[var(--color-accent)]/20 text-[var(--color-fg)]"
              : "text-[var(--color-muted)] hover:text-white"
          }`}
        >
          {w.label}
        </button>
      ))}
      {/* Выбор уже сменился, а числа ещё за прежнее окно — это видно словами, а
          не догадкой по мигнувшей таблице. */}
      {picked && shown && picked !== shown && (
        <span className="text-[10px] normal-case text-amber-300">
          показано за {flowWindowLabel(shown)}…
        </span>
      )}
    </span>
  );
}

/**
 * Цвет знаковой величины: зелёный в плюс, красный в минус.
 *
 * По ПРЕФИКСУ, а не по разбору числа: строка уже собрана в lib с типографским
 * минусом, и второй разбор был бы вторым местом, где решается знак.
 */
function pnlColor(value: string | null): string | undefined {
  if (!value) return undefined;
  if (value.startsWith("+")) return "var(--color-buy)";
  if (value.startsWith("−")) return "var(--color-sell)";
  return undefined;
}

/**
 * Держатели шеров СЕЙЧАС — второй вопрос того же экрана.
 *
 * Намеренно другой список, а не копия первого: это БАЛАНСЫ, куда входят шеры,
 * полученные минтом пары, а не покупкой. Замер 2026-08-10 дал 227 держателей
 * против 770 наторговавших, так что расхождение по одному кошельку само есть
 * сигнал — значит, он минтил, а не покупал.
 *
 * Свёрнут по умолчанию: он справочный, а удвоенная высота панели стоила бы
 * прокрутки к тому, ради чего панель открывают. Тот же список, но по отмеченным
 * кошелькам, живёт подпунктом строки «Итого» — и считается той же функцией.
 */
function HoldersBlock({
  p,
  tokens,
  tiers,
}: {
  p: ParticipantsStats | undefined;
  tokens: readonly Token[];
  tiers: Readonly<Record<string, WalletTier>>;
}) {
  const [open, setOpen] = useState(false);
  const groups = holderGroups(p, tokens);
  if (groups.length === 0) return null;

  return (
    <div className="mt-3 border-t border-[var(--color-border)] pt-2">
      <button
        onClick={() => setOpen((v) => !v)}
        className="text-xs text-[var(--color-muted)] hover:text-white"
        title={
          "БАЛАНСЫ, а не накопление сделками: сюда входят шеры, полученные минтом пары. " +
          "Расхождение с таблицей выше по одному кошельку означает, что он минтил, а не покупал"
        }
      >
        {open ? "▾" : "▸"} Держатели сейчас
      </button>

      {open && (
        <div className="mt-2">
          <HoldersTables groups={groups} tiers={tiers} />
          {/* Оговорка про «Набрано» и «Ср. цену»: обе берутся из ПОКАЗАННЫХ
              строк накопления, а их сотня и они могут быть отфильтрованы по
              пометкам. Прочерк там означает «этого кошелька нет среди
              показанных», а не «не торговал», и молчать об этом нельзя. */}
          <p className="mt-2 text-xs text-[var(--color-muted)]">
            «Набрано» и «Ср. цена» берутся из показанных строк накопления — тех,
            что в таблице выше. Прочерк в них означает «кошелька нет среди
            показанных», а не «не торговал».
          </p>
        </div>
      )}
    </div>
  );
}

/**
 * Число роли со знаком. null — выборка обрезана, и это НЕ ноль: ноль соврал бы,
 * что кошелёк не бил.
 */
function signed(value: string | null): React.ReactNode {
  if (value === null)
    return <span className="text-[var(--color-muted)]">—</span>;
  const negative = value.startsWith("-");
  return (
    <span style={{ color: negative ? "var(--color-sell)" : undefined }}>
      {value}
    </span>
  );
}

/**
 * Цвет исхода. Up зелёным, Down красным — как стороны в ленте: на пятиминутном
 * окне «вверх» и «покупка» это одно и то же движение.
 *
 * Незнакомый исход остаётся без цвета: выкрасить его наугад значило бы приписать
 * направление, которого мы не знаем.
 */
function outcomeColor(outcome: string): string | undefined {
  const o = outcome.toLowerCase();
  if (o === "up" || o === "yes") return "var(--color-buy)";
  if (o === "down" || o === "no") return "var(--color-sell)";
  return undefined;
}

/**
 * Отметка одного кошелька в группу.
 *
 * Галочка, а не подсветка строки нажатием: строка здесь уже нажимается —
 * раскрывает список сделок, — и второй смысл у того же нажатия означал бы, что
 * оператор попадает не туда каждый раз, когда торопится.
 */
function PickBox({ wallet }: { wallet: string }) {
  const picked = useParticipantPicks((s) => s.picked);
  const toggle = useParticipantPicks((s) => s.toggle);
  return (
    <input
      type="checkbox"
      checked={picked.has(wallet.toLowerCase())}
      onChange={() => toggle(wallet)}
      className="h-3 w-3 cursor-pointer accent-[var(--color-accent)]"
      title="сложить этот кошелёк в строку «Итого» наверху таблицы"
      aria-label="отметить кошелёк"
    />
  );
}

/**
 * Отметить или снять все ПОКАЗАННЫЕ строки.
 *
 * Показанные, а не все кошельки окна: отметить можно только то, что видно, —
 * иначе итог считался бы по строкам, которых на экране нет, и проверить его было
 * бы нечем.
 */
function PickAll({ lines }: { lines: readonly ParticipantLine[] }) {
  const picked = useParticipantPicks((s) => s.picked);
  const setMany = useParticipantPicks((s) => s.setMany);
  const shown = lines.map((l) => l.wallet);
  const marked = shown.filter((w) => picked.has(w.toLowerCase())).length;
  const all = shown.length > 0 && marked === shown.length;
  return (
    <input
      type="checkbox"
      checked={all}
      // Наполовину отмеченная таблица — своё состояние: следующее нажатие
      // ДОБИРАЕТ остальных, а не снимает отмеченных. Без этого признака галочка
      // выглядела бы пустой при девяноста отмеченных из ста.
      ref={(el) => {
        if (el) el.indeterminate = marked > 0 && !all;
      }}
      onChange={() => setMany(shown, !all)}
      className="h-3 w-3 cursor-pointer accent-[var(--color-accent)]"
      title="отметить все показанные строки — либо снять отметку со всех"
      aria-label="отметить все показанные"
    />
  );
}

/**
 * Все пометки одного кошелька: выведенные системой и ручные, плюс «поставить».
 *
 * # Почему в одной ячейке
 *
 * Потому что вопрос к ним один — «кто это». Тир, подгруппа сводки и ручная
 * пометка отвечают на него с разных сторон, но читают их одним взглядом, и
 * разведённые по колонкам они заставляли бы собирать одну мысль из двух мест.
 *
 * Разница между выведенным и произнесённым при этом не стёрта: тир и подгруппу
 * считают и переписывают обходы, ручную пометку не выводит ничто — и об этом
 * говорят подсказки самих чипов. Системные стоят ПЕРВЫМИ и в постоянном
 * порядке: чипы просматривают столбиком, и плавающий порядок сделал бы это
 * невозможным.
 *
 * # Почему чип нажимается, а не только показывается
 *
 * Потому что вопрос, который задают, увидев пометку, ровно один: «а кто ещё
 * такой?» Нажатие включает фильтр по ней — то же действие, что в списке фильтра
 * наверху, только под рукой. Так ведут себя И ручные чипы, и чип подгруппы: оба
 * фильтра применяются на сервере до отсечки в сто строк, то есть отвечают на
 * один и тот же вопрос одинаково.
 *
 * Тир — исключение и единственный чип, который не нажимается: он подмешивается
 * ПОСЛЕ отсечки (по показанным строкам), и фильтр по нему резал бы сотню
 * крупнейших, а не рынок, — то есть отвечал бы на другой вопрос тем же
 * движением.
 */
function TagCell({
  wallet,
  tags,
  tier,
  profile,
}: {
  wallet: string;
  tags: readonly string[];
  tier: WalletTier | undefined;
  profile: ParticipantCohortKind | undefined;
}) {
  const [open, setOpen] = useState(false);
  const state = useParticipantTags((s) => s.state);
  const cycle = useParticipantTags((s) => s.cycle);
  const apply = useApplyWalletTags();

  return (
    <span className="relative inline-flex flex-wrap items-center gap-1">
      {/* Тира нет вовсе — значка нет: участник рынка мог с нами ни разу не
          торговать, а тир считается по нашим встречам (ADR-0007). */}
      <Tier
        tier={tier}
        hideEmpty
        title="НАША квалификация кошелька — по нашим с ним встречам, а не по его торговле вообще"
      />
      <CohortChip kind={profile} />
      {tags.map((tag) => (
        <TagChip
          key={tag}
          tag={tag}
          state={state(tag)}
          onPick={() => cycle(tag)}
          onRemove={() => apply.mutate({ addresses: [wallet], remove: [tag] })}
        />
      ))}
      {/* Кнопка есть ВСЕГДА, а не только у помеченных: пометку ставят как раз
          тому, у кого её нет, и прятать её за наведением значило бы прятать
          главное действие колонки. */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="rounded px-1 text-[10px] text-[var(--color-muted)] hover:text-white"
        title="поставить пометку этому кошельку"
        aria-expanded={open}
      >
        +
      </button>
      {open && (
        <TagEditor
          addresses={[wallet]}
          current={tags}
          onClose={() => setOpen(false)}
        />
      )}
    </span>
  );
}

/**
 * Чип пометки: сама метка, её состояние в фильтре и крестик снятия.
 *
 * Формой похож на тир и подгруппу (класс `.tier`), но подсказка говорит, чем
 * отличается: те две выведены обходами и переписываются ими, а эту произнёс
 * человек. Разница дороже сходства — по выведенной величине нельзя узнать
 * кошелёк, которого узнал ты сам.
 */
function TagChip({
  tag,
  state,
  onPick,
  onRemove,
}: {
  tag: string;
  state: TagState;
  onPick: () => void;
  onRemove: () => void;
}) {
  return (
    <span
      className={`tier inline-flex items-center gap-0.5 ${
        state === "include"
          ? "bg-[var(--color-accent)]/20 text-[var(--color-fg)]"
          : state === "exclude"
            ? "text-[var(--color-sell)] line-through"
            : ""
      }`}
    >
      <button
        type="button"
        onClick={onPick}
        title={
          "РУЧНАЯ пометка оператора. Нажатие включает фильтр по ней: " +
          "показать только помеченных, потом убрать помеченных, потом снять"
        }
        className="hover:text-white"
      >
        {tag}
      </button>
      <button
        type="button"
        onClick={onRemove}
        title="снять пометку с этого кошелька"
        className="text-[var(--color-muted)] hover:text-[var(--color-sell)]"
        aria-label={`снять пометку ${tag}`}
      >
        ×
      </button>
    </span>
  );
}

/**
 * Окошко постановки пометки — на один кошелёк либо на всю отмеченную группу.
 *
 * # Почему список существующих, а не одно поле ввода
 *
 * Потому что пометка ценна только тогда, когда она ОДНА И ТА ЖЕ. Свободный ввод
 * без списка рождает «картель», «картель2» и «Картель » — три пометки вместо
 * одной, и фильтр по любой из них находит треть. Регистр и пробелы нормализует
 * сервер, но опечатку он отличить не может: её отличает глаз, если поставить
 * список перед глазами.
 */
function TagEditor({
  addresses,
  current,
  onClose,
}: {
  addresses: readonly string[];
  /** Пометки, уже стоящие на цели. У группы — те, что есть хотя бы у одного. */
  current: readonly string[];
  onClose: () => void;
}) {
  const dict = useWalletTagDict();
  const apply = useApplyWalletTags();
  const [draft, setDraft] = useState("");

  const put = (tag: string) => {
    const value = tag.trim();
    if (value === "") return;
    apply.mutate({ addresses, add: [value] });
    setDraft("");
  };

  return (
    <>
      {/* Подложка закрывает окошко нажатием мимо. Прозрачная и на весь экран:
          иначе закрыть его можно было бы только повторным попаданием в ту же
          кнопку в один пиксель шириной. */}
      <span
        className="fixed inset-0 z-20"
        onClick={onClose}
        aria-hidden="true"
      />
      <span
        className="absolute left-0 top-full z-30 mt-1 w-56 rounded border border-[var(--color-border)] bg-[var(--color-panel)] p-2 shadow-lg"
        onKeyDown={(e) => {
          if (e.key === "Escape") onClose();
        }}
      >
        <span className="mb-1 block text-[10px] text-[var(--color-muted)]">
          {addresses.length === 1
            ? "пометить кошелёк"
            : `пометить ${addresses.length} отмеченных`}
        </span>
        <input
          autoFocus
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") put(draft);
          }}
          placeholder="новая пометка"
          className="w-full rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-1 py-0.5 text-xs"
        />
        {/* Отказ говорится словами прямо здесь: пометка — действие, у которого
            нет другого следа на экране, и молчаливый отказ выглядел бы как
            успех. Сервер отвечает текстом (слишком длинный тег, запятая). */}
        {apply.isError && (
          <span className="mt-1 block text-[10px] text-[var(--color-sell)]">
            {(apply.error as Error).message}
          </span>
        )}
        <span className="mt-2 block max-h-40 overflow-y-auto">
          {(dict.data ?? []).map((t) => {
            const on = current.includes(t.tag);
            return (
              <button
                key={t.tag}
                type="button"
                onClick={() =>
                  apply.mutate(
                    on
                      ? { addresses, remove: [t.tag] }
                      : { addresses, add: [t.tag] },
                  )
                }
                className={`flex w-full items-baseline justify-between px-1 py-0.5 text-left text-[11px] hover:bg-[var(--color-accent)]/10 ${
                  on ? "text-[var(--color-fg)]" : "text-[var(--color-muted)]"
                }`}
                title={
                  on
                    ? "снять эту пометку"
                    : "поставить эту пометку"
                }
              >
                <span>
                  {on ? "✓ " : ""}
                  {t.tag}
                </span>
                {/* Сколько кошельков уже помечено — отличает живую пометку от
                    опечатки, поставленной однажды и забытой. */}
                <span className="num text-[10px] text-[var(--color-muted)]">
                  {t.wallets}
                </span>
              </button>
            );
          })}
          {(dict.data ?? []).length === 0 && (
            <span className="block text-[10px] text-[var(--color-muted)]">
              пометок пока нет — наберите первую
            </span>
          )}
        </span>
      </span>
    </>
  );
}

/**
 * Список фильтра по пометкам — в шапке карточки.
 *
 * # Почему две кнопки на тег, а не одна
 *
 * Вопросов к пометке два: «покажи только этих» и «убери этих». Второй не
 * выражается через первый — «все, кроме разобранных» потребовало бы выбрать
 * поимённо все остальные теги, включая те, которых ещё нет. Раньше оба вопроса
 * решались одной кнопкой по кольцу «показать → убрать → снять», и это была
 * ошибка: кольцо нигде не написано на экране, второе действие приходилось
 * находить перебором, а промах мимо нужного состояния означал ещё два нажатия.
 * Две кнопки называют оба действия словами и стоят ровно одного нажатия каждое.
 *
 * # Почему кнопка есть ДО первой пометки
 *
 * Потому что невидимая функция — это отсутствующая функция. До 2026-09-02
 * список прятался, пока не поставлена первая пометка, — довод был «фильтровать
 * нечего», а следствием стало то, что оператор фильтра просто не нашёл. Пустой
 * список теперь говорит, чем ставится первая пометка, и это ответ, а не отказ.
 */
function TagFilter() {
  const [open, setOpen] = useState(false);
  const dict = useWalletTagDict();
  const state = useParticipantTags((s) => s.state);
  const set = useParticipantTags((s) => s.set);
  const groupState = useParticipantTags((s) => s.groupState);
  const setGroup = useParticipantTags((s) => s.setGroup);
  const clear = useParticipantTags((s) => s.clear);
  // Счётчик считает ОБА вида пометок: фильтр на экране один, и «теги · 1» при
  // двух нажатых означало бы, что одно из них забыто.
  const active = useParticipantTags((s) => s.active)();
  const tags = dict.data ?? [];

  return (
    <span className="relative flex items-baseline gap-1">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        title="фильтр по РУЧНЫМ пометкам: показать только помеченных либо убрать их"
        className={`rounded px-1 text-[10px] normal-case ${
          active > 0
            ? "bg-[var(--color-accent)]/20 text-[var(--color-fg)]"
            : "text-[var(--color-muted)] hover:text-white"
        }`}
        aria-expanded={open}
      >
        теги{active > 0 ? ` · ${active}` : ""}
      </button>
      {/* Снять фильтр — ОТДЕЛЬНОЙ кнопкой снаружи списка, а не пунктом внутри:
          забытый фильтр это худший вид вранья на терминале (числа верны, картина
          мира нет), и снятие обязано быть одним нажатием, без открывания. */}
      {active > 0 && (
        <button
          type="button"
          onClick={clear}
          title="снять фильтр по пометкам целиком"
          className="text-[10px] text-[var(--color-muted)] hover:text-white"
        >
          ×
        </button>
      )}
      {open && (
        <>
          <span
            className="fixed inset-0 z-20"
            onClick={() => setOpen(false)}
            aria-hidden="true"
          />
          <span className="absolute right-0 top-full z-30 mt-1 max-h-64 w-64 overflow-y-auto rounded border border-[var(--color-border)] bg-[var(--color-panel)] p-2 shadow-lg">
            {/* Шапка списка — это подписи двух столбцов кнопок, а не украшение:
                по ним и понятно, что делает каждая. */}
            <span className="mb-1 flex items-baseline justify-between gap-2 text-[10px] text-[var(--color-muted)]">
              <span>ПОДГРУППА сводки</span>
              <span className="shrink-0">показать · исключить</span>
            </span>
            {/* Системные подгруппы — ПЕРВЫМИ и ВСЕ восемь, а не только
                встреченные в этом окне: список подгрупп задан правилом, а не
                составом рынка, и исчезающий пункт читался бы как «таких больше
                не бывает». Порядок тот же, что в сводке ниже: её и читают рядом.

                Тира в списке нет намеренно: он подмешивается ПОСЛЕ отсечки в
                сто строк, и фильтр по нему резал бы сотню крупнейших, а не
                рынок, — то есть отвечал бы на другой вопрос той же кнопкой. */}
            {COHORT_KINDS.map((kind) => (
              <FilterRow
                key={kind}
                // Полное имя, а не короткое с чипа: в списке места хватает, а
                // «смеш.−» без соседей по столбцу не читается.
                label={TITLES[kind]}
                title={
                  `${TITLES[kind]} — подгруппа сводки за всю накопленную глубину ` +
                  "кошелька, при умолчальной ступени порога оборота. Фильтр режет " +
                  "рынок ДО отсечки в сто строк, то есть находит и мелких"
                }
                state={groupState(kind)}
                onSet={(next) => setGroup(kind, next)}
              />
            ))}
            <span className="mb-1 mt-2 flex items-baseline justify-between gap-2 text-[10px] text-[var(--color-muted)]">
              <span>РУЧНЫЕ пометки</span>
              <span className="shrink-0">показать · исключить</span>
            </span>
            {tags.map((t) => (
              <FilterRow
                key={t.tag}
                label={t.tag}
                count={t.wallets}
                title={`пометка «${t.tag}»: стоит на ${t.wallets} кошельках`}
                state={state(t.tag)}
                onSet={(next) => set(t.tag, next)}
              />
            ))}
            {tags.length === 0 && (
              // Не «пусто», а КАК завести первую: список без пометок иначе
              // читается как поломка фильтра.
              <span className="block text-[10px] text-[var(--color-muted)]">
                пометок пока нет. Первая ставится кнопкой «+» в колонке «Теги»
                любой строки — или сразу группе: отметьте кошельки галочками и
                нажмите «пометить» в строке «Итого»
              </span>
            )}
          </span>
        </>
      )}
    </span>
  );
}

/**
 * Строка списка фильтра: имя пометки и две кнопки её состояния.
 *
 * Общая для ручных пометок и подгрупп сводки НАМЕРЕННО. Величины разные —
 * подгруппу вывел обход, пометку произнёс человек, — но вопрос к ним один и тот
 * же («показать таких» либо «убрать таких»), и две копии этой разметки
 * разъехались бы первым же исправлением: одну кнопку поправили, вторую забыли.
 *
 * Повторное нажатие СНИМАЕТ своё состояние: иначе снять один пункт из трёх можно
 * было бы только крестиком, который снимает фильтр целиком.
 */
function FilterRow({
  label,
  count,
  title,
  state,
  onSet,
}: {
  label: string;
  /** Сколько кошельков помечено. Нет числа — нет и подписи. */
  count?: number;
  title: string;
  state: TagState;
  onSet: (next: TagState) => void;
}) {
  return (
    <span className="flex items-baseline justify-between gap-2 px-1 py-0.5 text-[11px]">
      <span
        className={`truncate ${
          state === "include"
            ? "text-[var(--color-fg)]"
            : state === "exclude"
              ? "text-[var(--color-sell)] line-through"
              : "text-[var(--color-muted)]"
        }`}
        title={title}
      >
        {label}
        {count !== undefined && (
          <span className="num ml-1 text-[10px] text-[var(--color-muted)]">
            {count}
          </span>
        )}
      </span>
      <span className="flex shrink-0 items-baseline gap-1">
        <button
          type="button"
          onClick={() => onSet(state === "include" ? "off" : "include")}
          title={
            state === "include"
              ? `показывать всех: снять «только ${label}»`
              : `оставить в таблице только «${label}»`
          }
          aria-pressed={state === "include"}
          className={`rounded px-1 ${
            state === "include"
              ? "bg-[var(--color-accent)]/20 text-[var(--color-fg)]"
              : "text-[var(--color-muted)] hover:text-white"
          }`}
        >
          ✓
        </button>
        <button
          type="button"
          onClick={() => onSet(state === "exclude" ? "off" : "exclude")}
          title={
            state === "exclude"
              ? `вернуть «${label}» в таблицу`
              : `убрать из таблицы «${label}»`
          }
          aria-pressed={state === "exclude"}
          className={`rounded px-1 ${
            state === "exclude"
              ? "bg-[var(--color-sell)]/20 text-[var(--color-sell)]"
              : "text-[var(--color-muted)] hover:text-white"
          }`}
        >
          −
        </button>
      </span>
    </span>
  );
}

/**
 * Строка «Итого»: суммарная позиция отмеченных кошельков.
 *
 * # Почему она в шапке и почему первой
 *
 * Потому что это ответ, ради которого отмечают. `sticky` стоит на всём `thead`,
 * значит итог виден, докуда ни долистай сотню строк, — а сравнивают его именно с
 * дальними строками («эти четверо держат больше, чем весь остальной верх?»).
 *
 * # Что здесь НЕ складывается
 *
 * Средняя цена и РОЙ — они считаются заново из сумм, а не усредняются
 * (lib/participanttotals). Колонки «После нас» и «Цена» в режиме «против нас»
 * оставлены пустыми намеренно: первая — это роль ПОСЛЕДНЕЙ встречи, вторая —
 * сдвиг цены от НЕЁ ЖЕ, и у группы с разными встречами ни у той, ни у другой нет
 * суммы, которая бы что-то значила.
 */
function TotalsRow({
  totals,
  against,
  holdersOpen,
  onToggleHolders,
}: {
  totals: TotalsLine;
  against: boolean;
  holdersOpen: boolean;
  onToggleHolders: () => void;
}) {
  const clear = useParticipantPicks((s) => s.clear);
  const [tagging, setTagging] = useState(false);
  const note = totalsNote(totals);
  const title = totalsTitle(totals);

  return (
    /* Подложка на ЯЧЕЙКАХ и непрозрачная: строка прилипшая, а прежний
       `bg-[var(--color-bg)]/60` на самой строке был двойной ошибкой — фон
       строки при прилипании не уезжает, да и шестьдесят процентов пропускали
       под собой уходящие вверх строки кошельков. Цвет тот же с точностью до
       глаза: те же 60 % фона страницы, но подмешанные в панель заранее. */
    <tr className="border-y border-[var(--color-border)] text-left align-top [&>th]:bg-[color-mix(in_srgb,var(--color-bg)_60%,var(--color-panel))]">
      <th className="w-[1%] py-1 pr-2 font-normal">
        {totals.byPicks && (
          <button
            type="button"
            onClick={clear}
            className="text-[10px] text-[var(--color-muted)] hover:text-white"
            title="снять все отметки"
          >
            ×
          </button>
        )}
      </th>
      <th className="py-1 pr-3 font-normal" colSpan={2}>
        <span className="flex flex-wrap items-baseline gap-2">
          {/* Полнота знания — ПОДСКАЗКОЙ на самом слове «Итого»: чего в сумме не
              хватает, спрашивают, уже усомнившись в числе, а строка под ним
              стоила бы высоты всегда. */}
          <span className="text-[var(--color-fg)]" title={title}>
            Итого
          </span>
          {/* Основание по отмеченным — РЯДОМ с заголовком, а не подсказкой: сумма
              по четверым из шести отмеченных читается как сумма по шестерым, и
              заметить подмену можно только по этой строке. Пусто, когда отметок
              нет вовсе: итог по всем показанным — умолчание панели. */}
          {note && (
            <span className="text-[10px] font-normal text-[var(--color-muted)]">
              {note}
            </span>
          )}
          <button
            type="button"
            onClick={onToggleHolders}
            className="text-[10px] text-[var(--color-muted)] hover:text-white"
            title={
              "что из этого группа ДЕРЖИТ сейчас: балансы, средняя цена набора и " +
              "стоимость по последней цене. Балансы и накопление — разные величины: " +
              "в баланс входят шеры, полученные минтом пары"
            }
            aria-expanded={holdersOpen}
          >
            {holdersOpen ? "▾" : "▸"} держатели сейчас
          </button>
          {/* Групповая правка пометок — здесь, у выделения, а не отдельной
              кнопкой в шапке карточки: помечают то, что отмечено, и два разных
              места для «выделить» и «пометить» разводили бы одно движение. */}
          {totals.wallets.length > 0 && (
            <span className="relative inline-block">
              <button
                type="button"
                onClick={() => setTagging((v) => !v)}
                className="text-[10px] text-[var(--color-muted)] hover:text-white"
                title="поставить пометку всем кошелькам, вошедшим в этот итог"
                aria-expanded={tagging}
              >
                пометить
              </button>
              {tagging && (
                <TagEditor
                  addresses={totals.wallets}
                  // Пометки, стоящие на ВСЕХ сложенных кошельках: по ним окошко
                  // решает, что делает нажатие. Тег, который есть у половины,
                  // группе не принадлежит — нажатие ДОБИРАЕТ остальных.
                  current={totals.tagsAll}
                  onClose={() => setTagging(false)}
                />
              )}
            </span>
          )}
          {/* Предсказание — ЗДЕСЬ, а не отдельными колонками: колонок в таблице
              семнадцать, и три новых стоили бы ширины у каждой из сотни строк
              ради чисел, которые есть только у итога. Отделено чертой, потому
              что слева от неё подписи и кнопки, а справа — числа. */}
          <Forecast totals={totals} />
        </span>
      </th>
      <th
        className="py-1 pr-3 font-normal"
        style={{ color: outcomeColor(totals.outcome) }}
      >
        {totals.outcome || <span className="text-[var(--color-muted)]">—</span>}
      </th>
      <th className="num py-1 pr-3 text-right font-normal">
        {totals.net}
        {/* Те же шеры в деньгах — мелко под ними, как в сводке по группам: шеры
            отвечают «что группа держит», деньги — «во что ей это встало». */}
        {totals.netUsd && (
          <span className="block text-[10px] font-normal text-[var(--color-muted)]">
            {totals.netUsd}
          </span>
        )}
      </th>
      {/* Тем же `signed`, что в строке кошелька: у группы роль так же бывает
          отрицательной (раздавал больше, чем брал), и красный минус здесь обязан
          читаться так же, как строкой ниже. */}
      <th className="num py-1 pr-3 text-right font-normal">
        {totals.taker === null ? <Dash /> : signed(totals.taker)}
      </th>
      <th className="num py-1 pr-3 text-right font-normal">
        {totals.maker === null ? <Dash /> : signed(totals.maker)}
      </th>
      <th className="num py-1 pr-3 text-right font-normal text-[var(--color-muted)]">
        {totals.avg || <Dash />}
        {totals.bought && (
          <span
            className="block text-[10px] font-normal"
            title="вес средней: сколько шеров этого исхода группа купила"
          >
            {totals.bought}
          </span>
        )}
      </th>
      <th className="num py-1 pr-3 text-right font-normal text-[var(--color-muted)]">
        {totals.trades}
      </th>
      <th
        className="num py-1 pr-3 text-right font-normal"
        style={{ color: pnlColor(totals.settled) }}
        title={
          totals.settledRows < totals.rows
            ? `сложены только те ${totals.settledRows} строк, у которых итог окна известен`
            : "сумма итогов окна по всем сложенным кошелькам"
        }
      >
        {totals.settled ?? <Dash />}
      </th>
      {against && (
        <>
          <th className="num border-l border-[var(--color-border)] py-1 pl-3 pr-3 text-right font-normal">
            {totals.took ?? <Dash />}
          </th>
          {/* Две пустые ячейки — намеренно. См. доккомментарий компонента. */}
          <th className="py-1 pr-3 text-right font-normal">
            <Dash />
          </th>
          <th className="py-1 pr-3 text-right font-normal">
            <Dash />
          </th>
        </>
      )}
      <th
        className="num border-l border-[var(--color-border)] py-1 pl-3 pr-3 text-right font-normal"
        title={flowTotalsTitle(totals)}
      >
        {totals.bets ?? <Dash />}
      </th>
      <th
        className="num py-1 pr-3 text-right font-normal"
        title={flowTotalsTitle(totals)}
      >
        {totals.avgBet ?? <Dash />}
      </th>
      <th
        className="num py-1 pr-3 text-right font-normal"
        style={{ color: pnlColor(totals.pnl) }}
        title={flowTotalsTitle(totals)}
      >
        {totals.pnl ?? <Dash />}
      </th>
      <th
        className="num py-1 text-right font-normal"
        style={{ color: pnlColor(totals.roi) }}
        title={flowTotalsTitle(totals)}
      >
        {totals.roi ?? <Dash />}
      </th>
      <th
        className="num py-1 pl-3 text-right font-normal"
        style={{ color: pnlColor(totals.paid) }}
        title={flowTotalsTitle(totals)}
      >
        {totals.paid ?? <Dash />}
      </th>
    </tr>
  );
}

/**
 * Три числа предсказания в строке «Итого»: во что группа верит и что дешевле.
 *
 * # Почему это отдельный компонент, а не три ячейки в TotalsRow
 *
 * Потому что здесь читается СТАКАН, а он тикает. Правило то же, что у часов
 * (см. `useNow` и довод в шапке панели): держи чтение стакана в корне панели —
 * и каждое обновление книги перерисовывало бы таблицу на сотню строк ради трёх
 * чисел. В листе перерисовывается лист.
 *
 * # Почему селекторы узкие и отдают строки
 *
 * Селектор, возвращающий объект, создавал бы новую ссылку на каждом тике, и
 * сравнение по ссылке в zustand перестало бы работать — тот же приём, что в
 * IndexRow: две отдельные подписки, каждая на одно значение.
 *
 * Подписка на рынок держится страницей выше (MarketPage → useMarketSubscription
 * по condition_id, покрывает оба токена), поэтому стакан здесь есть и тогда,
 * когда лестницы свёрнуты.
 */
function Forecast({ totals }: { totals: TotalsLine }) {
  const asset = totals.forecastAsset;
  const ask = useBooks((s) =>
    asset ? (s.books[asset]?.asks[0]?.price ?? null) : null,
  );
  const status = useBooks((s) =>
    asset ? (s.books[asset]?.status ?? "absent") : "absent",
  );
  const edge = forecastEdge(totals, ask);

  // Предсказания нет вовсе — рынок не бинарный либо покупок не было. Молча
  // ничего: прочерки на месте, где раньше не было ничего, читались бы как
  // поломка, а не как «этот рынок так не считается».
  if (totals.forecast === "") return null;

  return (
    <span className="flex items-baseline gap-2 border-l border-[var(--color-border)] pl-2">
      <span className="num" title={forecastTitle(totals)}>
        {totals.forecast}
        {totals.forecastOutcome && (
          <span
            className="ml-1"
            style={{ color: outcomeColor(totals.forecastOutcome) }}
          >
            {totals.forecastOutcome}
          </span>
        )}
      </span>
      <span
        className="num text-[var(--color-muted)]"
        title={stakeTitle(totals)}
      >
        {totals.stake}
      </span>
      <span
        className="num"
        /* Цвет только у решённого исхода. Ниже порога число остаётся белым и
           остаётся на экране: спрятать его значило бы спрятать причину, по
           которой сигнала нет (см. FORECAST_DECIDED). */
        style={{
          color: edge.decided ? pnlColor(edge.label) : "var(--color-fg)",
        }}
        title={edgeTitle(totals, ask, status)}
      >
        {edge.label ?? <Dash />}
      </span>
    </span>
  );
}

/**
 * Подсказка правых колонок итога.
 *
 * Оговорка здесь обязательна и она не про точность: глубина наблюдения у
 * кошельков РАЗНАЯ — у одного сутки, у другого неделя, — и Σ пнл по ним есть
 * сумма за разные периоды. Число «у скольких свод есть» — знаменатель, без
 * которого сумма выглядит полной.
 */
function flowTotalsTitle(t: TotalsLine): string {
  if (t.flowRows === 0) {
    return "своды на коротких рынках не читались ни по одному из сложенных кошельков";
  }
  return (
    `сложено по ${t.flowRows} из ${t.rows} кошельков — тем, у кого свод есть. ` +
    "Глубина наблюдения у них РАЗНАЯ (у одного сутки, у другого неделя), " +
    "поэтому это сумма за разные периоды. Средняя на окно и РОЙ не усреднялись, " +
    "а посчитаны заново из сумм"
  );
}

/**
 * Подпункт итога: держатели среди сложенных кошельков.
 *
 * # Чем отличается от блока внизу панели
 *
 * Составом, и только им: тот показывает верхушку держателей рынка, этот — тех из
 * них, кто вошёл в итог. Считаются оба одной функцией (lib/holders), потому что
 * иначе два места считали бы одну и ту же среднюю цену.
 */
function PickedHoldersRow({
  p,
  tokens,
  totals,
  tiers,
  colSpan,
}: {
  p: ParticipantsStats | undefined;
  tokens: readonly Token[];
  totals: TotalsLine;
  tiers: Readonly<Record<string, WalletTier>>;
  colSpan: number;
}) {
  const only = useMemo(
    () => new Set(totals.wallets.map((w) => w.toLowerCase())),
    [totals.wallets],
  );
  const groups = holderGroups(p, tokens, only);
  const empty = groups.every((g) => g.rows.length === 0);

  return (
    <tr className="border-b border-[var(--color-border)] bg-[var(--color-bg)]/40">
      <td colSpan={colSpan} className="px-3 py-2">
        {p?.holders_reason ? (
          // Отказ источника называется словами: пустое место читалось бы как
          // «группа ничего не держит», а это другой факт.
          <p className="text-xs text-amber-300">{p.holders_reason}</p>
        ) : empty ? (
          <p className="text-xs text-[var(--color-muted)]">
            {/* Отсутствие в списке — НЕ «не держит»: источник отдаёт верхушку по
                балансу, и мелкий держатель в неё не попадает. */}
            никто из сложенных кошельков не виден в списке держателей: они либо
            раздали позицию, либо не попали в верхушку списка по балансу
          </p>
        ) : (
          <HoldersTables groups={groups} tiers={tiers} />
        )}
      </td>
    </tr>
  );
}

/**
 * Таблицы держателей по исходам — общая разметка двух мест панели.
 *
 * Колонки отвечают на вопрос целиком: КТО держит, ЧТО держит, СКОЛЬКО, ПО КАКОЙ
 * цене набирал и сколько это стоит сейчас. Раньше здесь были три колонки
 * (адрес, имя, баланс), и на «по какой цене» ответа не было вовсе — хотя оба
 * слагаемых уже приезжали в том же ответе.
 */
function HoldersTables({
  groups,
  tiers,
}: {
  groups: readonly HolderGroup[];
  tiers: Readonly<Record<string, WalletTier>>;
}) {
  return (
    <div className="grid gap-4 sm:grid-cols-2">
      {groups.map((g) => (
        <div key={g.assetID}>
          <p className="mb-1 text-xs" style={{ color: outcomeColor(g.outcome) }}>
            {g.outcome || "исход неизвестен"}{" "}
            <span className="text-[var(--color-muted)]">
              {/* «Из сложенных в итог», а не «из отмеченных»: подпункт итога
                  показывает тех, кто вошёл в сумму, а без единой отметки в неё
                  входят все показанные строки — и слово «отмеченных» тогда
                  называло бы отбор, которого не было. */}
              — {g.rows.length}{" "}
              {g.onlyPicked ? "из сложенных в итог" : `из ${g.count} по балансу`}
              {/* Насыщение выдачи называется словами: список из ровно потолка
                  строк — это верхушка, а не состав, и отсутствие кошелька в нём
                  ничего не доказывает. */}
              {g.saturated && !g.onlyPicked && " (выдача насыщена)"}
              {g.rows.length > 0 && ` · Σ ${g.total}`}
              {g.totalValue && ` · ${g.totalValue}`}
            </span>
          </p>
          {g.rows.length === 0 ? (
            <p className="text-[11px] text-[var(--color-muted)]">никого</p>
          ) : (
            <table className="w-full text-xs">
              <thead className="text-left text-[var(--color-muted)]">
                <tr>
                  {/* Одна колонка на имя и адрес — как в таблице выше: это один
                      ответ на один вопрос (см. WalletLabel). */}
                  <th className="pb-1 pr-2 font-normal">Имя</th>
                  <th className="pb-1 pr-2 text-right font-normal">Баланс</th>
                  <th
                    className="pb-1 pr-2 text-right font-normal"
                    title="доля от суммы ПОКАЗАННЫХ строк этого исхода, а не от выпуска токена"
                  >
                    Доля
                  </th>
                  <th
                    className="pb-1 pr-2 text-right font-normal"
                    title="средняя цена ПОКУПОК этого токена в этом окне. Прочерк — здесь не покупал"
                  >
                    Ср. цена
                  </th>
                  <th
                    className="pb-1 pr-2 text-right font-normal"
                    title="баланс по последней цене принта. Не пнл: во что позиция оценивается сейчас"
                  >
                    Стоимость
                  </th>
                  <th
                    className="pb-1 text-right font-normal"
                    title={
                      "что кошелёк набрал СДЕЛКАМИ по этому токену. Баланс заметно " +
                      "больше — значит, шеры пришли МИНТОМ пары, а не покупкой. " +
                      "Прочерк означает «строки кошелька нет среди показанных», " +
                      "а не «не торговал»"
                    }
                  >
                    Набрано
                  </th>
                </tr>
              </thead>
              <tbody>
                {g.rows.map((h) => (
                  <tr
                    key={h.wallet}
                    className={`border-t border-[var(--color-border)]${
                      h.ours ? " bg-[var(--color-accent)]/10" : ""
                    }`}
                  >
                    <td className="max-w-[18ch] py-1 pr-2">
                      <span className="inline-flex max-w-full items-center gap-1">
                        <WalletLabel address={h.wallet} name={h.name} />
                        <Tier tier={tiers[h.wallet.toLowerCase()]} hideEmpty />
                        {h.ours && (
                          <span className="shrink-0 text-[var(--color-accent)]">мы</span>
                        )}
                      </span>
                    </td>
                    <td className="num py-1 pr-2 text-right">{h.amount}</td>
                    <td className="num py-1 pr-2 text-right text-[var(--color-muted)]">
                      {h.share || <Dash />}
                    </td>
                    <td className="num py-1 pr-2 text-right text-[var(--color-muted)]">
                      {h.avg || <Dash />}
                    </td>
                    <td className="num py-1 pr-2 text-right">
                      {h.value || <Dash />}
                    </td>
                    <td className="num py-1 text-right text-[var(--color-muted)]">
                      {h.traded ?? <Dash />}
                      {/* Минт — ОТДЕЛЬНАЯ пометка, а не догадка читателя по двум
                          числам: расхождение баланса и накопления и есть сигнал,
                          ради которого второй список стоит рядом с первым. */}
                      {h.minted && (
                        <span
                          className="ml-1 text-[10px] text-amber-300"
                          title={
                            "баланс заметно больше набранного сделками: шеры пришли " +
                            "МИНТОМ пары (split), а минт сделкой не является и в " +
                            "историю не попадает"
                          }
                        >
                          минт
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ))}
    </div>
  );
}
